/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ManagerSchedulePage.tsx
 * PURPOSE: Màn Bảng phân ca — SC-009-SCHEDULE-GRID (Release v0 WP-6b)
 *          Biến thể A (tuần hiện tại) + B (tuần trống + gợi ý [19]) + C (tuần quá
 *          khứ read-only + banner [20]) + guard PUBLISHED D-6 (badge kép [8b],
 *          khoá thao tác tới khi [Xuất bản lại]) + nhập [17]→[18-import]
 *          + popup [18] "Có thay đổi chưa công bố" (chặn điều hướng tới khi
 *          [Xuất bản lại]/[Hủy thay đổi]; [Hủy thay đổi] khôi phục về bản
 *          xuất bản gần nhất qua schedule_publish_snapshots — lượt 13)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Xếp ca tuần (Vietnamese F&B Workforce Management)
 * - Impact: UX Critical + Compliance — trần giờ/tuần, nghỉ giữa 2 ca,
 *   D-6 guard PUBLISHED, mode "Chặn" chặn xuất bản khi còn vi phạm
 * - Stakeholders: Branch Managers, Shift Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: React + TanStack Query (useManagerSchedule hooks) — mọi data từ API
 *   thật (manager_schedule.py WP-6a); skeleton + error + empty states;
 *   roving tabindex (←→↑↓ · Enter/Space); mobile <768 view-theo-ngày
 * - Dependencies: React Router, useManagerSchedule, ShiftCellPopover,
 *   ScheduleImportModal, NQH design tokens
 *
 * @cross-references
 * - Mockup chuẩn: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/grid.html
 * - Biến thể: .../grid-past-week.html · .../grid-empty-week.html · .../grid-import.html
 * - Popup [18]: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-009 dòng 3350 (mockup) · 3419 (ANNOTATION) · 3606/3629-3635 (STATE) ·
 *   3723 (chặn điều hướng) · 4029 (ngoại lệ mobile R7)
 * - Bộ lọc "Nhân viên" [11c]/[11d]: nhãn nút 4 biến thể + AND vế chọn/tìm +
 *   `[11d]` (lưới rỗng do chuỗi tìm không khớp) vẫn theo WIREFRAME-V0-MASTER-
 *   REGISTRY.md §SC-009 ANNOTATION `[11c]`/`[11d]`; RIÊNG kích thước tấm
 *   (340px) + vị trí "Tất cả (bỏ chọn)" (menu__item NGOÀI EmployeePicker) +
 *   chân panel (đếm SỐ ĐÃ CHỌN) đi theo prototype sc009-build-prototype.html
 *   `menuEmp()` — `[11c]` LỖI THỜI trên 3 điểm này (bê nhầm mẫu SC-011 260px
 *   phẳng), coordinator đồng bộ lại registry riêng.
 * - [Hủy thay đổi] (lượt 13): publish_week() chụp snapshot toàn bộ `schedules`
 *   trong phạm vi vào bảng `schedule_publish_snapshots` ngay sau khi set
 *   PUBLISHED; discard_week_changes() (manager_schedule.py) đọc bản GẦN NHẤT,
 *   ghi lại đúng shift_template_id/giờ/trạng thái cũ (sửa), khôi phục CANCELLED
 *   → PUBLISHED (xoá), và huỷ (CANCELLED) mọi ca không có trong snapshot
 *   (thêm sau publish). Model: backend/app/models/schedule.py (SchedulePublishSnapshot).
 * - Hooks: frontend/src/hooks/useManagerSchedule.ts
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py
 * - Toolbar dropdown viewport-clip fix (2026-09-14, @tester Playwright+hit-test
 *   xác nhận thật): 4 dropdown [1a] (Nhân viên/Khu vực/Xem/Xuất) đổi từ
 *   `.menuAnchor{position:relative}`+`.menu{position:absolute}` (bị `.filterBar`
 *   overflow-x:auto/overflow-y:hidden cắt đứt) sang components/common/
 *   PortalMenu.tsx (portal + position:fixed) — cùng pattern đã dùng ở
 *   ManagerAttendancePage.tsx/ManagerStaffPage.tsx. Gỡ kèm listener
 *   `document.addEventListener("click", ...)` cấp trang (chồng cơ chế đóng với
 *   PortalMenu — xem comment tại vị trí cũ trong component).
 * - GỘP "component bộ lọc Calendar" + BỎ dropdown "Xem: Tuần ▼" (2026-09-14,
 *   @coder, theo xác nhận trực tiếp coordinator — KHÔNG còn là đề xuất):
 *   xoá hẳn `ViewMode`/`viewMode` — lưới giờ CHỈ có MỘT khái niệm "khoảng
 *   đang xem" (`rangeFromISO`/`rangeToISO`), N ngày bất kỳ (trần 210 ngày,
 *   khớp WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 [CAL]), LUÔN sửa được bất kể
 *   N (annotation [1]: "không có chế độ danh sách phẳng chỉ-xem"). Cụm điều
 *   hướng ngày `‹ [nhãn] ›` dùng `ContinuousScrollDatePicker` (component dùng
 *   chung [CAL], đã có sẵn ở ManagerAttendancePage.tsx) làm nhãn giữa —
 *   `‹`/`›` bước ±7 ngày giữ nguyên ĐỘ DÀI khoảng (không mở lịch), nhãn giữa
 *   mở `[CAL]` để chọn khoảng tuỳ ý. Dữ liệu lưới giờ LUÔN đi qua
 *   `useMultiWeekGrid` (gộp N tuần lịch chạm khoảng đã chọn — kể cả N=1),
 *   không còn nhánh `useWeeklyGrid` đơn tuần riêng. "Nhập"/"Xuất ▼" (backend
 *   chỉ nhận `week_start` đơn — `manager_schedule.py` các endpoint
 *   `weekly-grid/import/*`+`weekly-grid/export`) chỉ hiện khi khoảng đang xem
 *   khớp ĐÚNG một tuần máy chủ (`isSingleWeekView`) — ẩn khi khoảng trải
 *   nhiều tuần/không tròn tuần, tránh áp dụng NGẦM chỉ 1 phần khoảng đang
 *   hiển thị (KHÔNG tự chế loop-nhập-theo-tuần — rủi ro ghi sai/trùng, cần
 *   @architect thiết kế endpoint gộp trước). "Xuất bản"/"Hủy thay đổi" LẶP
 *   tuần tự qua từng tuần server-week chạm khoảng (mỗi lệnh gọi vẫn atomic/
 *   idempotent Y HỆT single-week — publish_week()/discard_week_changes() đã
 *   luôn nhận `week_start` đơn, KHÔNG đổi backend) — dialog liệt kê rõ các
 *   tuần sẽ xuất bản (wireframe [PUB] "Phạm vi xuất bản khi khoảng không
 *   trùng tuần lịch"), kết quả sau vòng lặp báo rõ N/M tuần thành công + lý
 *   do từng tuần thất bại (không có "ghi nửa vời" mập mờ — mỗi tuần độc lập,
 *   atomic, kết quả minh bạch). Badge/guard D-6 gộp qua OR (bất kỳ tuần nào
 *   dirty → khoá) — suy luận riêng @coder, CHƯA phải xác nhận @cpo/@ba, xem
 *   PR description.
 * - 4 rủi ro/giới hạn lượt trước tự báo cáo (2026-09-14, tiếp, @coder) — xử
 *   lý theo yêu cầu coordinator, đồng bộ wireframe làm SAU (không phải việc
 *   lượt này):
 *   (1) `week_start_day` tenant KHÁC Thứ 2 — GIẢI, không cần backend mới:
 *   `areaOptionsQuery` (đã gọi sẵn cho `rangeFromISO`) VỐN nhận về
 *   `rules.week_start_day` (backend luôn trả, FE type trước chỉ chưa khai —
 *   xem ScheduleRulesInfo managerApi.ts); `touchedWeekStarts` đổi từ
 *   `mondayOf` hardcode sang `weekStartOf` (scheduleShared.ts, mirror
 *   `_normalize_week_start` backend) dùng giá trị thật. Khoảng MẶC ĐỊNH ban
 *   đầu (`defaultRangeFromISO`) CỐ Ý vẫn neo Thứ 2 lịch — ngoài phạm vi rủi ro
 *   này, xem comment tại khai báo.
 *   (2) Lệch hàng khi danh sách NV khác nhau giữa các tuần trong khoảng — GIẢI:
 *   renderCell()/renderGrid()/renderMobileDayview()/kéo-chọn (`finishDrag`)
 *   đổi từ định vị `emp.days[index]` (mảng RIÊNG từng hàng, độ dài có thể khác
 *   nhau) sang định vị theo NGÀY THẬT qua `viewDates` (cột dùng chung) +
 *   `emp.days.find(d => d.date === …)`. NV không có dữ liệu ngày nào đó →
 *   ô CHỈ-XEM (không suy ra ngữ cảnh roster, khoá ghi thay vì đoán).
 *   (3) [Nhập]/[Xuất ▼] chỉ hiện khi `isSingleWeekView` — GIẢI RIÊNG PHẦN
 *   XUẤT (chỉ đọc, an toàn lặp gọi + tải N file riêng, xem comment
 *   handleExport): hiện bất kể khoảng trải bao nhiêu tuần. [Nhập] GIỮ NGUYÊN
 *   khoá cũ — GHI dữ liệu từ 1 file người dùng tải lên, không có cách an toàn
 *   tự tách đúng dòng theo tuần từ FE (file không có cột ngày để suy tuần;
 *   suy luận sai → ghi nhầm ca sang tuần khác) — cần @architect thiết kế
 *   trước, KHÔNG tự chế ở đây.
 *   (4) Mobile dayview (renderMobileDayview, viewport di động) — VERIFY bằng
 *   Playwright thật trên `http://localhost:2500/manager/schedule` (tenant
 *   `rel-v0-e2e-shared`), viewport 390×844, khoảng >7 ngày — hiện đúng N
 *   ngày, cuộn được, ô vẫn bấm-sửa được sau các fix (1)+(2). Không phát sinh
 *   bug mới cần sửa thêm.
 * - [Sao chép lịch] khoảng ngày tuỳ ý (2026-09-14, tiếp #3, @coder) — nút
 *   toolbar đổi từ gọi thẳng `handleCopyPreviousWeek` (đã XOÁ, chỉ chép ĐÚNG
 *   1 tuần→1 tuần) sang mở `PortalMenu` 3 lựa chọn ("Sao chép tuần trước" /
 *   "Sao chép tháng trước" / "Sao chép theo khoảng ngày…", prototype
 *   `openCopyMenu`) → tính nguồn mặc định theo lựa chọn (`chooseCopySource`) →
 *   mở `CopyScheduleModal` (2 endpoint mới `copy-range-preview`/`copy-range-commit`,
 *   generalize `copy-from-week` — xem backend `manager_schedule.py`). Nút mờ
 *   (`styles.btnLocked`, KHÔNG `disabled`/`aria-disabled` — xem comment
 *   `publishVisuallyLocked` cho lý do .btn[aria-disabled] tự pointer-events:none)
 *   khi khoảng ĐÍCH đang xem đã qua hẳn (`isCopyTargetFullyPast`, tính riêng
 *   theo `rangeToISO < todayISO` — KHÔNG tái dùng `isPastWeek`, biến đó
 *   aggregate theo TUẦN-MÁY-CHỦ chạm khoảng xem nên có thể sai phạm vi, xem
 *   comment tại khai báo `isCopyTargetFullyPast`). `useCopyScheduleFromWeek`
 *   (hook) + endpoint `copy-from-week` GIỮ NGUYÊN (không còn caller trong file
 *   này nhưng KHÔNG xoá — theo yêu cầu, có thể còn dùng/dự phòng nơi khác).
 * ===============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  useDiscardScheduleWeekChanges,
  useMultiWeekGrid,
  usePublishScheduleWeek,
  useReviewWeekProposals,
  useScheduleShiftCodes,
  useWeeklyGrid,
} from "../../hooks/useManagerSchedule";
import {
  managerApi,
  type GridDayCell,
  type ImportCommitResult,
  type WeeklyGridData,
  IMPORT_SKIP_REASON_LABELS_VI,
} from "../../services/api/managerApi";
import ShiftCellPopover, { contentChipClass } from "./schedule/ShiftCellPopover";
import EmployeePicker, { type EmployeePickerItem } from "../../components/manager/EmployeePicker";
import ScheduleImportModal from "./schedule/ScheduleImportModal";
import QuickEntryModal from "./schedule/QuickEntryModal";
import CopyScheduleModal from "./schedule/CopyScheduleModal";
import ScheduleAuditPanel from "./schedule/ScheduleAuditPanel";
import type { BulkAssignResult, BulkOffResult } from "../../services/api/managerApi";
import {
  addDaysISO,
  extractApiError,
  fmtHours,
  formatDateVi,
  formatDayMonth,
  formatWeekRange,
  mondayOf,
  noDiacritics,
  parseISODate,
  toISODate,
  weekStartOf,
  type ApiErrorInfo,
} from "./schedule/scheduleShared";
// ISS-MGR-3 (2026-08-09, mở rộng): 3 tooltip trong lưới đều nằm trong
// .gridWrap { overflow-x: auto } — cùng root cause đã vá ở ManagerAttendancePage
// + ManagerStaffPage, tái dùng nguyên component, không cần quyết định thiết kế mới.
import { PortalTooltip } from "../../components/common/PortalTooltip";
// [CAL] Cụm chọn khoảng ngày dùng CHUNG với ManagerAttendancePage.tsx (Luật 13,
// docs/02-design/04-ui-ux-design/UI-UX-DECISION-REGISTER.md §2 mục 13) — port
// nguyên component, KHÔNG dựng lại cơ chế `‹ ›` cũ (đúng ghi chú ở đầu
// ContinuousScrollDatePicker.tsx: "NEXT screen needing a date/range filter
// should reuse this component"). Trần 210 ngày RIÊNG cho SC-009 (khớp
// WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 [CAL] "210 ngày ở [1c]/[1d]" —
// KHÁC trần mặc định 92 của component).
import { ContinuousScrollDatePicker, type DateFilterValue } from "../../components/common/ContinuousScrollDatePicker";
// BUG THẬT đã xác nhận bằng Playwright + hit-test trên browser thật (2026-09-14,
// @tester): 4 dropdown toolbar [1a] (Nhân viên/Khu vực/Xem/Xuất) dùng
// `.menuAnchor{position:relative}` + `.menu{position:absolute}` — bị `.filterBar`
// (cha trực tiếp, overflow-x:auto; overflow-y:hidden, cao 44px theo đúng ý đồ
// "toolbar 1 dòng cuộn ngang") CẮT ĐỨT, vô hình/không bấm được dù logic bên trong
// đúng. Cùng root cause đã vá ở ManagerAttendancePage/ManagerStaffPage — port
// PortalMenu (portal + position:fixed, thoát mọi ancestor overflow:hidden/auto),
// không viết lại cơ chế mới.
import { PortalMenu, type PortalMenuAnchor } from "../../components/common/PortalMenu";
import styles from "./ManagerSchedulePage.module.css";

// BUG THẬT đã sửa (2026-08-12, user báo): ca bị Từ chối MẤT hẳn dấu hiệu
// "Chờ duyệt" (chỉ style riêng cho PENDING) rồi hiện y hệt 1 ca bình thường
// đã duyệt — trông như "biến mất" khỏi trạng thái chờ xử lý dù dữ liệu vẫn
// còn. Thêm style riêng cho REJECTED để phân biệt rõ 3 trạng thái.
// [EXP] (2026-09-07): "Hết hạn" — đề xuất PENDING đã qua giờ bắt đầu ca (xem
// managerApi.ts ProposalApprovalStatus + backend _is_proposal_expired). Viền
// nét đứt XÁM riêng (khác coffee của "Chờ duyệt") — đúng thứ tự ưu tiên màu
// nền chip trong ANNOTATION [1-chip]: Chờ duyệt > Hết hạn > OT > Phân theo giờ > mã ca.
function approvalChipClass(approvalStatus: string | null | undefined): string {
  if (approvalStatus === "PENDING") return styles.shiftChipPending;
  if (approvalStatus === "EXPIRED") return styles.shiftChipExpired;
  if (approvalStatus === "REJECTED") return styles.shiftChipRejected;
  return "";
}

// prototype .shift-chip--started (sc009-build-prototype.html:136) — mốc "đã
// bắt đầu" CÙNG NGƯỠNG với [R42] backend (_is_write_locked_by_r42/
// _is_proposal_expired: `ngày + giờ bắt đầu <= thời điểm hiện tại`, so tới
// phút, không vùng đệm). Tính phía FE vì day.date + shift.start_time đã đủ dữ
// liệu — không cần trường mới từ backend. Dùng giờ LOCAL trình duyệt, cùng
// convention với các so sánh "hôm nay" khác trong file này (vd
// todayISO = toISODate(new Date())), không tự quy đổi múi giờ VN.
const hasShiftStarted = (dateISO: string, startTime: string): boolean =>
  new Date(`${dateISO}T${startTime}:00`) <= new Date();

// EXPIRED/REJECTED đã tự mang opacity riêng (ý nghĩa "hết hạn"/"bị từ chối",
// khác "đã bắt đầu") — không gắn thêm .shiftChipStarted lên 2 loại này để
// tránh 2 khai báo opacity cùng đè 1 chip (xem .shiftChipStarted trong .module.css).
const startedChipClass = (
  dateISO: string,
  startTime: string,
  approvalStatus: string | null | undefined,
): string =>
  approvalStatus !== "EXPIRED" && approvalStatus !== "REJECTED" && hasShiftStarted(dateISO, startTime)
    ? styles.shiftChipStarted
    : "";

// ── Inline SVG icons (Heroicons — copy nguyên path từ mockup, 0 emoji) ──────

const ChevronDownIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);

// Popup [18] "Có thay đổi chưa công bố" — nút thu gọn [▾]/[▴] (WIREFRAME-V0-
// MASTER-REGISTRY.md §SC-009 dòng 3350-3363).
const ChevronUpIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// [1a-range] ANNOTATION: nhãn phạm vi "giữ icon lịch" — 0 emoji, cùng convention
// SVG trên (khớp CalendarIcon của DateRangePicker.tsx, tái dùng path).
const CalendarIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={16} height={16} aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path strokeLinecap="round" d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

const WarnIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

const LockIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const MailIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const InfoIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} width={13} height={13} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const DotsIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18} aria-hidden="true">
    <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
  </svg>
);

// ── Helpers ────────────────────────────────────────────────────────────────

/** Lượt 5 (2026-09-07): cột "Nhân viên" — wireframe ANNOTATION [1] + Luật 11
 * ("Cặp 'Mã + Tên' dùng đúng một định dạng `<Mã> • <Tên>`") đòi hỏi hiện
 * `{mã NV} • {tên NV}`, không chỉ tên suông. Một số NV có thể chưa được gán
 * mã (`employee_code === null`) — khi đó chỉ hiện tên, KHÔNG hiện "• " thừa. */
const formatEmployeeLabel = (employeeCode: string | null | undefined, fullName: string): string =>
  employeeCode ? `${employeeCode} • ${fullName}` : fullName;

/** Số tuần ISO (label [3] khi chưa có data) */
const isoWeekNumber = (iso: string) => {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

/**
 * BUG THẬT đã sửa (2026-08-09): toast import trước đây chỉ hiện
 * "Đã nhập N gán lịch" (đọc `result.imported`), bỏ qua `skipped`/
 * `skipped_reasons` backend đã trả sẵn — Manager không biết vì sao preview
 * báo N dòng hợp lệ nhưng nhập vào ít hơn. Hiện rõ số bị bỏ qua + lý do.
 */
const formatImportResultToast = (result: ImportCommitResult): string => {
  if (result.skipped <= 0) {
    return `Đã nhập ${result.imported} gán lịch`;
  }
  const reasonParts = Object.entries(result.skipped_reasons)
    .map(([reason, count]) => `${count} ${IMPORT_SKIP_REASON_LABELS_VI[reason] ?? reason}`)
    .join(", ");
  return `Đã nhập ${result.imported} gán lịch — bỏ qua ${result.skipped} dòng (${reasonParts})`;
};

/** Toast cho popup [+] Phân ca hàng loạt (VĐ1, 2026-08-16) — đủ số gán +
 * số bỏ qua (trùng lặp HOẶC chồng-giờ/nghỉ-tối-thiểu) + tên chi nhánh khi
 * điều động, cùng cách trình bày với formatImportResultToast phía trên. */
const formatBulkAssignToast = (result: BulkAssignResult): string => {
  const branchPart = result.is_cross_branch && result.target_branch_name
    ? ` tại ${result.target_branch_name}`
    : "";
  // `skipped` gồm CẢ trùng-mã-ca LẪN chồng-giờ/nghỉ-tối-thiểu (mode "Chặn")
  // — không hardcode nhãn cụ thể vì nguyên nhân bỏ qua có thể khác nhau.
  const skippedPart = result.skipped.length > 0 ? ` — bỏ qua ${result.skipped.length} (trùng ca/vi phạm ràng buộc)` : "";
  // mode "Chỉ cảnh báo" → ca vẫn được tạo nhưng có cảnh báo chồng-giờ/nghỉ-
  // tối-thiểu — báo riêng, KHÔNG lẫn vào skippedPart (ca đó đã nằm trong
  // assigned_count, không phải bị bỏ qua).
  const warningsPart = result.warnings.length > 0 ? `, ${result.warnings.length} cảnh báo vi phạm` : "";
  const notifyPart = result.notified_count > 0 ? `, đã báo ${result.notified_count} nhân viên` : "";
  return `Đã phân ca ${result.assigned_count} lượt${branchPart}${skippedPart}${warningsPart}${notifyPart}`;
};

/** Toast cho [2f] BR-SCHD-013 "Nghỉ hàng loạt" (2026-09-07) — cùng cách trình
 * bày với formatBulkAssignToast phía trên. */
const formatBulkOffToast = (result: BulkOffResult): string => {
  const skippedPart =
    result.skipped.length > 0
      ? ` — bỏ qua ${result.skipped.length} (ca đã bắt đầu, không xoá được)`
      : "";
  return `Đã cho nghỉ ${result.removed_count} lượt ca${skippedPart}`;
};

/** Tooltip ⓘ cột Tổng — verbatim mockup thead */
const TOTAL_TOOLTIP = "Tổng giờ ca trong tuần. Đỏ khi vượt trần giờ cho phép. Đổi trong Cấu hình lịch.";

interface ActiveCell {
  userId: string;
  dateISO: string;
  // [POS-FIX] `right` bổ sung — popover neo BÊN CẠNH ô (không phải NGAY DƯỚI
  // như bản port gốc từ common.js) để ô đang thao tác không bị chính popover
  // che khuất; xem anchorTo() trong ShiftCellPopover.tsx.
  anchor: { left: number; top: number; bottom: number; right: number };
  // Bổ sung 2026-08-17 (yêu cầu người dùng: "không được làm 2 modal khác
  // nhau") — hợp nhất RangeAssignPopover (kéo-chọn ô trống → khoảng ngày)
  // vào CHÍNH ShiftCellPopover: khi mở qua kéo-chọn, endDateISO khác
  // dateISO; khi mở qua click ô thường (có/không có ca), bằng dateISO
  // (mặc định 1 ngày, ShiftCellPopover cho sửa lại qua DateRangePicker).
  endDateISO?: string;
  // [2b]/[2f] kéo-chọn 2D (lia dọc qua nhiều hàng nhân viên) — >1 phần tử
  // báo cho ShiftCellPopover mở ở "chế độ vùng chọn" (banner rectangle +
  // Thêm ca hàng loạt/Nghỉ hàng loạt). Đây là NGUỒN DUY NHẤT của phạm vi ghi
  // hàng loạt — bộ lọc "Nhân viên" (`selectedEmployeeIds`) KHÔNG được truyền
  // vào ShiftCellPopover, giữ đúng vai trò VIEW-scope thuần (chỉ lọc hiển
  // thị, xem `filteredGroups`/`rangeRows`).
  rangeEmployeeIds?: string[];
  // PO 2026-09-13: "Cần hiển thị thông tin nhân viên nào đang được thêm ca
  // vào modal... thay vì chỉ sửa title đổi từ tên cụ thể sang '{N} nhân
  // viên'" — title chỉ hiện SỐ LƯỢNG (tránh title dài vô hạn), danh sách TÊN
  // đầy đủ hiện trong banner phạm vi của ShiftCellPopover. Song song với
  // rangeEmployeeIds (không suy ra được, cần snapshot CÙNG LÚC vì visibleRows
  // có thể đổi giữa lúc kéo-chọn và lúc render popover).
  rangeEmployeeNames?: string[];
}

/** Trần an toàn số tuần gộp cho lưới N-ngày (2026-09-14, kế thừa nguyên trần
 * S-MGR-VIEWMODE cũ) — tránh người dùng chọn khoảng ngày quá rộng (vd nhiều
 * năm) gây gọi API hàng loạt. 30 tuần × 7 ngày = 210 ngày, khớp ĐÚNG trần
 * `maxRangeDays` của [CAL] (wireframe §SC-009 [CAL] "210 ngày ở [1c]/[1d]") —
 * hai trần khớp nhau có chủ đích, không phải trùng hợp.
 * PARITY với backend COPY_RANGE_MAX_DAYS (manager_schedule.py) hiện là
 * CONVENTION — hai constant độc lập cùng giá trị, FE không đọc từ BE. Lộ trình
 * đã chốt 2026-09-23: khi mở rộng tenant-configurable (SAU v0, phải amend
 * registry §SC-009), chuyển cả hai phía sang đọc chung 1 nguồn server-side. */
const DATE_RANGE_MAX_WEEKS = 30;
const CAL_MAX_RANGE_DAYS = DATE_RANGE_MAX_WEEKS * 7;

/** Nhãn thứ tự Thứ 2..Chủ nhật — dùng để tính `day_label` header lưới N-ngày
 * client-side (xem `viewDates`), độc lập với `day_label` server trả trên
 * từng GridDayCell thật. */
const WEEKDAY_LABELS_VI = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

/** Ngày lưới N-ngày (2026-09-14) — GridDayCell + cờ `isPastWeek` mượn từ
 * chính tuần-máy-chủ chứa ngày đó (WeeklyGridData.is_past_week của tuần
 * nguồn). R42 (WIREFRAME §SC-009 [R42]) "chặn GHI theo NGÀY, không theo cả
 * khoảng" — nhưng backend hiện CHỈ gate ở mức TUẦN (đã ghi nhận là khoảng
 * cách đo được, xem [R42] cuối đoạn), nên đây là granularity ĐÚNG những gì
 * backend/API hiện cung cấp: khoá theo tuần-máy-chủ chứa ngày, không suy
 * thêm rule mới ở FE. */
interface MergedDayCell extends GridDayCell {
  isPastWeek: boolean;
}

/** Hàng nhân viên gộp nhiều tuần (2026-09-14) — thay `RangeRow` (cũ, chỉ-xem)
 * lẫn `GridEmployeeRow` (cũ, đơn tuần) làm MỘT kiểu duy nhất cho renderGrid/
 * renderCell/renderMobileDayview dùng chung bất kể N. */
interface MergedEmployeeRow {
  user_id: string;
  full_name: string;
  employee_code: string | null;
  work_area_id: string | null;
  work_area_name: string;
  days: MergedDayCell[];
  total_hours: number;
  /** OR gộp qua mọi tuần-máy-chủ NV này xuất hiện — mỗi tuần backend tự
   * tính is_over_cap theo ĐÚNG trần giờ TUẦN (không có "trần giờ khoảng" —
   * RB "trần giờ tuần vẫn tính theo TUẦN LỊCH chứa ngày, không theo khoảng
   * đang lọc"), nên KHÔNG suy luận trần mới ở đây, chỉ OR cờ đã có sẵn. */
  is_over_cap: boolean;
  transferred_from_branch_name: string | null;
}

interface MergedGroup {
  work_area_id: string | null;
  work_area_name: string;
  staff_count: number;
  employees: MergedEmployeeRow[];
}

// ── Component ──────────────────────────────────────────────────────────────

const ManagerSchedulePage: React.FC = () => {
  // 2026-09-14 (gộp "component bộ lọc Calendar" + bỏ dropdown "Xem: Tuần ▼"):
  // khoảng mặc định LUÔN floor về Thứ 2 lịch (mondayOf) NGAY TỪ ĐẦU — KHÁC
  // pattern ISS-PILOT-16 cũ (gửi neo THÔ, để server tự floor theo
  // week_start_day tenant). Lý do đổi: lưới N-ngày giờ LUÔN đi qua
  // useMultiWeekGrid (touchedWeekStarts bên dưới) — client phải biết TRƯỚC
  // ranh giới tuần để tính đúng danh sách tuần cần gọi; không còn "gọi đúng 1
  // tuần rồi đọc lại biên đã server-chuẩn-hoá" như nhánh "week mode" cũ.
  //
  // ⚠️ GIỚI HẠN ĐÃ BIẾT — ĐÃ GIẢI (Rủi ro 1, 2026-09-14 tiếp): việc gộp tuần
  // (`touchedWeekStarts`, khai báo bên dưới) giờ dùng ĐÚNG `week_start_day`
  // tenant thật (qua `weekStartOf` + `areaOptionsQuery.data.rules.week_start_day`
  // — field backend LUÔN trả, FE trước đây chỉ chưa khai báo type, xem
  // managerApi.ts), KHÔNG còn hardcode Thứ 2 — sửa dứt điểm chicken-and-egg mô
  // tả trước đây, HOÀN TOÀN từ FE, không cần hợp đồng backend mới.
  //
  // Phần CÒN LẠI cố ý GIỮ NGUYÊN (ngoài phạm vi Rủi ro 1): khoảng MẶC ĐỊNH
  // ban đầu (`defaultRangeFromISO` ngay dưới) vẫn floor về Thứ 2 LỊCH
  // (`mondayOf`), không phải Thứ-đầu-tuần-tenant — tại thời điểm khởi tạo
  // state, CHƯA có request nào chạy nên chưa thể biết `week_start_day` thật
  // (chicken-and-egg CHỈ còn đúng ở đây, không còn ở `touchedWeekStarts`).
  // Hệ quả: với tenant week_start_day≠1, khoảng MẶC ĐỊNH "Thứ 2..CN" có thể
  // trải 2 tuần-máy-chủ thật (touchedWeekStarts.length=2 ngay từ lúc mở
  // trang) — đây là kết quả ĐÚNG của việc gộp chính xác theo ranh giới thật,
  // không phải lỗi mới; [Nhập] ẩn tương ứng (`isSingleWeekView`, hành vi đã có
  // sẵn khi khoảng trải nhiều tuần — xem Rủi ro 3 ở header file); [Xuất ▼]
  // KHÔNG còn ẩn (Rủi ro 3 giải riêng phần đọc — tải 2 file thay vì 1). Đổi ý
  // nghĩa "mặc định" để luôn khớp trọn 1 tuần-máy-chủ đầu tiên là quyết định
  // UX riêng, ngoài phạm vi rủi ro này.
  const todayISO = toISODate(new Date());
  const defaultRangeFromISO = toISODate(mondayOf(parseISODate(todayISO)));
  const defaultRangeToISO = addDaysISO(defaultRangeFromISO, 6);
  const [areaId, setAreaId] = useState<string>("");
  // WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 [11b] dòng ~3505: mục lọc riêng
  // "Chỉ NV điều chuyển (N)" cuối menu Lọc khu vực (ẩn khi N=0) — port sentinel
  // pattern F_TRANSFERRED của sc009-build-prototype.html:864 (không phải
  // work_area_id thật nên lọc CLIENT-SIDE, không gửi lên server qua param
  // work_area_id — xem filteredGroups). Loại trừ với chọn khu vực thường.
  const [transferredOnly, setTransferredOnly] = useState(false);
  const [openMenu, setOpenMenu] = useState<"area" | "employee" | "export" | "copy" | null>(null);
  // Rect (viewport coords) của nút vừa mở menu — đo lúc bấm qua
  // `getBoundingClientRect()`, cùng kỹ thuật `setAuditLogAnchor(...)` ở
  // ManagerAttendancePage.tsx. Dùng CHUNG 1 state vì `openMenu` cũng chỉ 1 giá
  // trị tại một thời điểm (đúng 1 trong 4 menu mở — dropdown "Xem: Tuần ▼" đã
  // BỎ HẲN, xem header file; "copy" thêm 2026-09-14 tiếp #3).
  const [menuAnchor, setMenuAnchor] = useState<PortalMenuAnchor | null>(null);
  // Khoảng NGUỒN mặc định + cờ "modal Sao chép lịch đang mở" gộp 1 state —
  // null = đóng; set bởi `chooseCopySource` (1 trong 3 mục menu [Sao chép
  // lịch]). Khoảng ĐÍCH KHÔNG cần lưu riêng ở đây — CopyScheduleModal luôn
  // nhận thẳng `rangeFromISO`/`rangeToISO` hiện tại làm props (đúng nguyên
  // tắc "một nơi quyết định phạm vi" — modal không có bản sao khoảng đích
  // riêng có thể lệch khỏi thanh công cụ).
  const [copyModalSource, setCopyModalSource] = useState<{ from: string; to: string } | null>(null);
  // [1a-range]/[1e] khoảng đang xem — MỘT khái niệm duy nhất, N ngày bất kỳ
  // (không còn phân "week"/"dateRange"). `‹`/`›` bước ±7 ngày giữ nguyên N
  // (xem shiftRange); nhãn giữa mở [CAL] (ContinuousScrollDatePicker) để
  // chọn khoảng tuỳ ý, trần CAL_MAX_RANGE_DAYS (210 ngày).
  const [rangeFromISO, setRangeFromISO] = useState<string>(defaultRangeFromISO);
  const [rangeToISO, setRangeToISO] = useState<string>(defaultRangeToISO);
  // CAL (ContinuousScrollDatePicker) luôn tự đảm bảo from<=to lúc áp dụng
  // (pickDay/confirmRange) — nhánh này gần như không còn đường nào tới được
  // (không còn 2 ô <input type=date> tự do như bản dateRange cũ), giữ lại
  // THUẦN PHÒNG THỦ (an toàn hơn xoá hẳn) cho banner lỗi bên dưới.
  const rangeInvalid = rangeToISO < rangeFromISO;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [focusPos, setFocusPos] = useState<{ r: number; c: number }>({ r: 0, c: 0 });
  const [mobileDayIdx, setMobileDayIdx] = useState<number>(() => (new Date().getDay() + 6) % 7);

  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [publishDialog, setPublishDialog] = useState<"normal" | "late" | "blocked" | null>(null);
  const [publishError, setPublishError] = useState<ApiErrorInfo | null>(null);
  const [publishNotice, setPublishNotice] = useState(false);
  // Xuất bản/Hủy thay đổi khi khoảng đang xem trải NHIỀU tuần lịch (wireframe
  // [PUB] "Phạm vi xuất bản khi khoảng không trùng tuần lịch") LẶP TUẦN TỰ
  // qua publishMutation/discardChangesMutation cho từng tuần — 2 cờ RIÊNG
  // này đại diện "cả vòng lặp đang chạy" (thay vì đọc thẳng `.isPending` của
  // mutation dùng chung, vốn tắt/bật lại GIỮA mỗi lần gọi trong vòng lặp và
  // sẽ làm nút/spinner nhấp nháy sai).
  const [isPublishingBatch, setIsPublishingBatch] = useState(false);
  const [isDiscardingBatch, setIsDiscardingBatch] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // BR-SCHD-011 (2026-09-07, 🆕 CHƯA CÓ BR chính thức — wireframe là nguồn
  // đầu tiên): modal [5] "Nhập nhanh bằng văn bản".
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);
  // BR-SCHD-012 (2026-09-14, 🆕 CHƯA CÓ BR chính thức — wireframe là nguồn
  // đầu tiên): panel [6] "Nhật ký thay đổi lịch" — toggle, KHÔNG phải mở/đóng
  // hộp thoại (ANNOTATION [6]). Mount/unmount qua điều kiện render (không
  // dùng CSS ẩn/hiện) — mỗi lần BẬT là "nạp trang đầu" sạch, khớp STATE
  // NHẬT-KÝ-TẮT→NHẬT-KÝ-BẬT (state cục bộ của panel — tuần riêng, bộ lọc,
  // phân trang — tự reset theo đúng ý "mở lại từ đầu").
  const [auditOpen, setAuditOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // VĐ1 (2026-08-16, phương án 2 — thay popup "+ Phân ca" bằng kéo-chọn TRỰC
  // TIẾP trên lưới): bấm giữ 1 ô trống → lia chuột sang các ô khác → thả
  // chuột → popover chọn mã ca (+ chi nhánh điều động) cho cả vùng đã chọn.
  // Ô đã CÓ ca vẫn giữ nguyên hành vi cũ (click mở ShiftCellPopover xem/sửa/
  // xoá ca) — không đụng flow đó.
  // 2026-09-07 (BR-SCHD-013, sync wireframe [2b] "lia theo CẢ HAI CHIỀU —
  // ngang qua ngày, DỌC QUA NHÂN VIÊN"): thêm `endRowIdx` — trước đây kéo-chọn
  // CHỈ ngang trong 1 hàng (`onMouseEnter` chặn cứng `dragSelect.rowIdx !==
  // rowIdx`); nay cho lia dọc qua nhiều hàng, `rowIdx` giữ vai trò "hàng bắt
  // đầu" (điểm neo popover), `endRowIdx` là hàng con trỏ đang ở.
  const [dragSelect, setDragSelect] = useState<{
    rowIdx: number;
    endRowIdx: number;
    userId: string;
    fullName: string;
    startDayIdx: number;
    endDayIdx: number;
    anchorRect: { left: number; top: number; bottom: number; right: number };
  } | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Danh mục lọc (khu vực/nhân viên cho EmployeePicker) — LUÔN lấy từ tuần
  // ĐẦU của khoảng đang xem (không lọc theo khu vực), giữ NGUYÊN phạm vi cũ
  // ("1 tuần hiện tại") cả hai nhánh cũ ("week" lẫn "dateRange") đều đã dùng
  // — không mở rộng ngoài yêu cầu của việc này.
  const areaOptionsQuery = useWeeklyGrid({ week_start: rangeFromISO });
  const shiftCodesQuery = useScheduleShiftCodes();

  // Lưới N-ngày LUÔN gộp qua useMultiWeekGrid — kể cả khoảng đúng 1 tuần lịch
  // (N=7 mặc định). Thay THẲNG cho cặp gridQuery (đơn tuần)/multiWeekQueries
  // (nhiều tuần, chỉ-xem) cũ — một nguồn dữ liệu DUY NHẤT cho mọi N, khớp
  // ANNOTATION [1] "Lưới luôn ở dạng cột và luôn sửa được bất kể N".
  //
  // Rủi ro 1 GIẢI QUYẾT (2026-09-14, tiếp — trước đây neo cứng `mondayOf`,
  // xem lịch sử comment ở khai báo `defaultRangeFromISO` phía trên): đã tìm
  // ra đường lấy `week_start_day` tenant TỪ FE mà KHÔNG cần sửa backend —
  // `areaOptionsQuery` (khai báo ngay phía trên) VỐN ĐÃ gọi weekly-grid cho
  // đúng `rangeFromISO`, và response backend (ScheduleRulesInfo, ISS-PILOT-16)
  // LUÔN kèm `rules.week_start_day` — FE type trước đây chỉ CHƯA khai báo
  // field này (drift type-thiếu-field, không phải field mới, xem
  // ScheduleRulesInfo trong managerApi.ts). Dùng thẳng giá trị đó (qua
  // `weekStartOf`, mirror `_normalize_week_start` backend) thay cho
  // `mondayOf` hardcode Thứ 2 khi tính ranh giới tuần-máy-chủ để gộp.
  //
  // Cửa sổ ngắn trước khi `areaOptionsQuery` tải xong: fallback 1 (Thứ 2,
  // khớp TenantWorkforceConfig.DEFAULT_WEEK_START_DAY) — ĐÚNG giá trị backend
  // cũng dùng khi tenant chưa từng lưu cấu hình, nên fallback này không tự
  // chế hành vi mới, chỉ là độ trễ 1 nhịp render tới khi biết giá trị thật
  // (nếu khác 1, `touchedWeekStarts` tự tính lại đúng ngay khi data về —
  // useMultiWeekGrid phát lại đúng query cho các anchor mới, không cần thao
  // tác gì thêm từ người dùng).
  const weekStartDay = areaOptionsQuery.data?.rules.week_start_day ?? 1;
  const touchedWeekStarts = useMemo(() => {
    if (rangeInvalid) return [];
    const starts: string[] = [];
    let cursor = toISODate(weekStartOf(parseISODate(rangeFromISO), weekStartDay));
    const lastWeekStart = toISODate(weekStartOf(parseISODate(rangeToISO), weekStartDay));
    while (cursor <= lastWeekStart && starts.length < DATE_RANGE_MAX_WEEKS) {
      starts.push(cursor);
      cursor = addDaysISO(cursor, 7);
    }
    return starts;
  }, [rangeFromISO, rangeToISO, rangeInvalid, weekStartDay]);
  const weekQueries = useMultiWeekGrid(touchedWeekStarts, areaId || undefined);

  const publishMutation = usePublishScheduleWeek();
  const discardChangesMutation = useDiscardScheduleWeekChanges();
  const weekApprovalMutation = useReviewWeekProposals();
  const [weekApprovalOpen, setWeekApprovalOpen] = useState(false);
  const [weekRejectingUserId, setWeekRejectingUserId] = useState<string | null>(null);
  const [weekRejectReason, setWeekRejectReason] = useState("");

  // ── Gộp N tuần-máy-chủ (weekQueries) thành các khái niệm trang vẫn dùng ──
  const weeksLoading = weekQueries.some((q) => q.isLoading);
  const weeksError = weekQueries.some((q) => q.isError);
  const refetchWeeks = () => weekQueries.forEach((q) => q.refetch());
  const weeksData = useMemo(
    () => weekQueries.map((q) => q.data).filter((w): w is WeeklyGridData => !!w),
    [weekQueries],
  );
  // Tuần ĐẦU của khoảng đang xem — nguồn cho mọi thứ CỐ Ý giữ phạm vi 1-tuần
  // (branchLabel/rules tenant-level giống hệt mọi tuần; "Duyệt tuần" [Employee
  // tự đăng ký ca]; nhãn dialog xuất bản đơn-tuần) — KHÔNG mở rộng các luồng
  // đó ra nhiều tuần, ngoài phạm vi việc này.
  const data = weeksData[0];

  // bot-037 (GH#803): "Duyệt cả tuần" luôn nhắm ĐÚNG `data` (tuần đầu khoảng
  // xem, xem comment trên) — cùng lý do `isCopyTargetFullyPast` KHÔNG dùng
  // `isPastWeek` aggregate bên dưới, dùng thẳng cờ per-week `data.is_past_week`
  // (SSOT `sunday < vn_today()`, backend manager_schedule.py) để không lọt
  // trường hợp khoảng xem nhiều tuần mà chỉ tuần đầu đã khoá.
  const isApprovalTargetWeekLocked = !!data?.is_past_week;

  // isPastWeek/isEmptyWeek GIỮ NGUYÊN TÊN (nhiều chỗ dưới còn dùng) nhưng giờ
  // là AGGREGATE qua mọi tuần-máy-chủ khoảng đang xem chạm vào: "quá khứ" chỉ
  // đúng khi CẢ khoảng đều đã qua (R42 "chặn GHI, không chặn XEM... khoảng
  // vắt qua hôm nay thì phần đã qua chỉ-đọc, phần còn lại vẫn sửa được" — cấp
  // per-CELL đã được xử ở MergedDayCell.isPastWeek/renderCell, biến này chỉ
  // còn dùng cho banner [20] toàn trang khi TOÀN BỘ khoảng đã qua).
  const isPastWeek = weeksData.length > 0 && weeksData.every((w) => w.is_past_week);
  // [Sao chép lịch] mờ khi khoảng ĐÍCH đang xem đã qua hẳn — khớp ĐÚNG luật
  // backend `target_to < vn_today()` (`_compute_copy_range_plan`,
  // manager_schedule.py). CHÚ Ý: KHÔNG tái dùng `isPastWeek` ở trên cho việc
  // này — biến đó AGGREGATE theo TUẦN-MÁY-CHỦ chạm khoảng xem
  // (`touchedWeekStarts`), sai phạm vi cho câu hỏi này: 1 tuần-máy-chủ chạm
  // khoảng có thể còn NGÀY TƯƠNG LAI nằm NGOÀI `rangeToISO` (vd khoảng đang
  // xem chỉ 3 ngày cuối của 1 tuần chưa qua hết) → `isPastWeek=false` dù
  // `rangeToISO` thật sự đã qua — nút sẽ KHÔNG mờ dù bấm sẽ luôn 422. Tính
  // riêng, đúng đối tượng backend kiểm (`rangeToISO`, không phải tuần chứa nó).
  const isCopyTargetFullyPast = rangeToISO < todayISO;
  const isEmptyWeek = weeksData.length > 0 && weeksData.every((w) => w.status === "empty");
  const anyDirty = weeksData.some((w) => w.dirty_after_publish && !w.is_past_week);
  const allPublishedClean =
    weeksData.length > 0 && weeksData.every((w) => w.status === "published" && !w.dirty_after_publish);
  // [8]/[8b] badge khi khoảng gộp nhiều tuần — SUY LUẬN RIÊNG @coder (wireframe
  // không nói rõ cách hiện badge khi các tuần trong khoảng khác trạng thái
  // nhau — nêu rõ trong PR description để @cpo/@ba xác nhận lại):
  // có tuần nào dirty → badge kép ("cần xử lý ngay" thắng); mọi tuần có nội
  // dung ĐỀU "Đã xuất bản" sạch → "Đã xuất bản"; còn lại (chưa tuần nào công
  // bố, hoặc trộn công bố/chưa công bố mà không dirty) → "Chưa xuất bản".
  const isPublished = anyDirty || allPublishedClean;

  // ── BƯỚC 6 · Guard PUBLISHED (D-6): sửa sau công bố → khoá mọi thao tác
  // khác tới khi [Xuất bản lại]. Gộp qua OR — BẤT KỲ tuần nào trong khoảng
  // đang xem còn dirty là khoá TOÀN BỘ khoảng (hướng AN TOÀN hơn: đúng ý
  // nghĩa "còn thứ chưa công bố trong khoảng đang xem"). ──
  const guardLocked = anyDirty;

  // D-7 (2026-09-08, gộp nhiều tuần 2026-09-14): [Hủy thay đổi] khi khoảng
  // dirty trải NHIỀU tuần phải khôi phục ĐỦ mọi tuần dirty (xem
  // confirmDiscardChanges) — chỉ cho bấm khi TẤT CẢ tuần dirty đều có
  // snapshot khôi phục được; thiếu 1 tuần cũng khoá CẢ CỤM (an toàn hơn để
  // lọt 1 tuần khôi phục được còn N-1 tuần 422 giữa chừng).
  const dirtyWeeksData = useMemo(
    () => weeksData.filter((w) => w.dirty_after_publish && !w.is_past_week),
    [weeksData],
  );
  const canRestoreSnapshot =
    dirtyWeeksData.length > 0 && dirtyWeeksData.every((w) => w.has_restorable_snapshot);
  const discardDisabledReason =
    dirtyWeeksData.length > 0 && !canRestoreSnapshot
      ? "Một hoặc nhiều tuần trong khoảng này được xuất bản trước khi tính năng khôi phục ra mắt — bấm [Xuất bản lại] 1 lần để bắt đầu lưu bản khôi phục từ nay."
      : undefined;

  // ── Popup [18] "Có thay đổi chưa công bố" (WIREFRAME-V0-MASTER-REGISTRY.md
  // §SC-009 dòng 3350-3363, 3419, 3606, 3629-3635, 3723) — vòng đời của popup
  // TRÙNG với guardLocked: server đã cho biết "tuần có ca PUBLISHED + còn ca
  // DRAFT" (dirty_after_publish=true) là chính xác điều kiện popup phải mở;
  // không cần state "đã bắn popup chưa" riêng vì streak dirty MỚI luôn ứng với
  // guardLocked chuyển false→true (chỉ [Xuất bản lại] mới tắt được dirty —
  // xem Việc 1: "Hủy thay đổi" restore CHƯA có cơ chế, xem nút bên dưới).
  // `unpubCollapsed` chỉ là lựa chọn hiển thị THUẦN UI — thu gọn KHÔNG huỷ
  // chặn điều hướng (dòng 3723), và mỗi streak dirty MỚI luôn mở lại ở dạng
  // đầy đủ (không giữ collapsed cũ).
  const [unpubCollapsed, setUnpubCollapsed] = useState(false);
  useEffect(() => {
    setUnpubCollapsed(false);
  }, [guardLocked]);

  /** Chặn điều hướng (đổi khoảng/bộ lọc/chế độ xem/rời trang) trong khi popup
   * [18] đang treo — dòng 3723: "đổi khoảng, đổi bộ lọc, rời trang đều bị
   * chặn tới khi bấm [Hủy thay đổi] hoặc [Xuất bản lại]; thu gọn KHÔNG huỷ
   * chặn". Gọi ở ĐẦU mọi handler đổi khoảng/bộ lọc/chế độ xem. */
  const blockedByUnpub = () => {
    if (!guardLocked) return false;
    setToast('Xử lý "Có thay đổi chưa công bố" trước khi tiếp tục.');
    return true;
  };

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Chặn rời trang ở CẤP TRÌNH DUYỆT (đóng tab/refresh/gõ URL khác) khi popup
  // [18] đang chặn. Điều hướng TRONG SPA (menu trái sang SC-010/SC-011/
  // SC-012) render ở Layout cha, NGOÀI phạm vi component này — App.tsx dùng
  // `<BrowserRouter>` thường (không phải data router), nên `useBlocker`/
  // `unstable_usePrompt` của react-router-dom v7 không áp dụng được ở đây;
  // ghi nhận là giới hạn còn lại, không tự chế cơ chế chặn router ngoài
  // phạm vi Việc 2.
  useEffect(() => {
    if (!guardLocked) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [guardLocked]);

  // 2026-09-14 (@coder — port PortalMenu cho 4 dropdown toolbar [1a]): listener
  // "click ra ngoài → đóng" cấp TRANG này ĐÃ GỠ. Lý do: `PortalMenu` tự có cơ
  // chế đóng-khi-click-ngoài RIÊNG (mousedown + `ref.contains()`, xem
  // components/common/PortalMenu.tsx) — giữ song song 2 cơ chế sẽ CHỒNG NHAU và
  // gây đúng bug A2 (2026-09-03) đã vá thủ công cho menu "Nhân viên": listener
  // `click` cấp trang này không phân biệt click TRONG hay NGOÀI menu (không có
  // target-containment check) nên MỌI click — kể cả tick checkbox/gõ tìm bên
  // trong menu multi-select — đều đóng menu ngay lập tức. `PortalMenu.onClose`
  // (`() => setOpenMenu(null)`, gắn ở từng nút bấm bên dưới) nay là cơ chế đóng
  // DUY NHẤT khi click ra ngoài; giữ `onClick={(e) => e.stopPropagation()}` ở
  // nội dung menu "Nhân viên" chỉ còn là phòng thủ, không còn bug này để né nữa.

  // Đổi khoảng đang xem → reset popover/dialog/banner [21] + chip ngày mobile
  useEffect(() => {
    setActiveCell(null);
    setPublishDialog(null);
    setPublishError(null);
    setPublishNotice(false);
    const isDefaultCurrentWeek = rangeFromISO === defaultRangeFromISO && rangeToISO === defaultRangeToISO;
    setMobileDayIdx(isDefaultCurrentWeek ? (new Date().getDay() + 6) % 7 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeFromISO, rangeToISO]);

  const areaOptions = useMemo(
    () =>
      (areaOptionsQuery.data?.groups ?? []).map((g) => ({
        id: g.work_area_id ?? "",
        name: g.work_area_name,
      })),
    [areaOptionsQuery.data],
  );

  // Bộ lọc nhân viên (S-MGR-FILTER 2026-08-09) — hoàn thiện bộ lọc theo yêu
  // cầu: thêm chọn-nhiều-nhân-viên cạnh Khu vực. Danh sách lấy từ
  // areaOptionsQuery (không lọc theo khu vực) để luôn đủ toàn bộ NV trong
  // scope, không phụ thuộc bộ lọc Khu vực hiện tại.
  // A2 (2026-09-03, họp 25/08 mục 1): dữ liệu cho EmployeePicker — giữ nhóm
  // theo khu vực (đúng pattern "Giao việc"), dedup theo user_id phòng NV
  // xuất hiện ở >1 nhóm. `code` = employee_code — wireframe [11c]: dòng
  // trong tấm hiện `{mã NV} • {tên nhân viên}` (Luật 11), không chỉ tên suông.
  const employeePickerItems = useMemo<EmployeePickerItem[]>(() => {
    const seen = new Set<string>();
    const items: EmployeePickerItem[] = [];
    (areaOptionsQuery.data?.groups ?? []).forEach((g) => {
      g.employees.forEach((emp) => {
        if (seen.has(emp.user_id)) return;
        seen.add(emp.user_id);
        items.push({
          id: emp.user_id,
          name: emp.full_name,
          code: emp.employee_code,
          groupName: g.work_area_name,
        });
      });
    });
    return items.sort((a, b) => a.name.localeCompare(b.name, "vi"));
  }, [areaOptionsQuery.data]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  // wireframe [11c] "GỘP 2-TRONG-1": ô tìm chỉ có MỘT nơi (không có ô tìm rời
  // trên toolbar) NHƯNG chuỗi tìm phải THOÁT RA khỏi EmployeePicker (CONTROLLED
  // ở đây, không còn state nội bộ) — cần cho (a) nhãn nút ngoài `tìm "{chuỗi}"`
  // không mất dấu khi tấm đã đóng, (b) chuỗi tìm là VẾ THỨ HAI lọc LƯỚI, kết
  // hợp AND với `selectedEmployeeIds` (xem `searchMatchedIds`/`filteredGroups`).
  const [employeeSearch, setEmployeeSearch] = useState("");
  const toggleEmployeeFilter = (id: string) =>
    setSelectedEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  // A2: "Chọn cả nhóm" trong EmployeePicker — cộng/trừ cả mảng id vào lựa chọn
  const setEmployeeGroupFilter = (ids: string[], selected: boolean) =>
    setSelectedEmployeeIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (selected) next.add(id);
        else next.delete(id);
      });
      return Array.from(next);
    });
  // wireframe [11c]: chuỗi tìm khớp theo id — dùng để lọc LƯỚI (vế 2, AND với
  // selectedEmployeeIds) VÀ để quyết định STATE `TÌM-TÊN-KHÔNG-KHỚP` ([11d]).
  // `null` = không có chuỗi tìm đang áp (không lọc thêm gì cả).
  const searchMatchedEmployeeIds = useMemo(() => {
    const q = noDiacritics(employeeSearch.trim());
    if (!q) return null;
    const set = new Set<string>();
    employeePickerItems.forEach((item) => {
      if (noDiacritics(item.name).includes(q) || (item.code != null && noDiacritics(item.code).includes(q))) {
        set.add(item.id);
      }
    });
    return set;
  }, [employeePickerItems, employeeSearch]);
  // wireframe [11c]: nhãn nút "Nhân viên: …" — 4 biến thể nêu ĐỦ CẢ HAI VẾ
  // đang áp dụng (chọn NV cụ thể + chuỗi tìm), để không mất dấu bộ lọc khi
  // tấm đã đóng (ô tìm chỉ tồn tại BÊN TRONG tấm).
  const employeeSearchTrimmed = employeeSearch.trim();
  const employeeFilterLabel = (() => {
    const countPart = selectedEmployeeIds.length > 0 ? `${selectedEmployeeIds.length} đã chọn` : "";
    const searchPart = employeeSearchTrimmed ? `tìm "${employeeSearchTrimmed}"` : "";
    if (countPart && searchPart) return `${countPart} · ${searchPart}`;
    if (countPart) return countPart;
    if (searchPart) return searchPart;
    return "Tất cả";
  })();
  const employeeFilterActive = selectedEmployeeIds.length > 0 || employeeSearchTrimmed.length > 0;
  const clearEmployeeFilter = () => {
    setSelectedEmployeeIds([]);
    setEmployeeSearch("");
  };

  const branchLabel = data?.branch_code || data?.branch_name || "—";
  // weekNumber/weekEndISO/weekRange — GIỮ phạm vi tuần ĐẦU của khoảng đang
  // xem, dùng cho các luồng CỐ Ý còn đơn-tuần (dialog "Duyệt tuần" [Employee
  // tự đăng ký ca] — ngoài phạm vi việc này). "Xuất bản"/"Hủy thay đổi" dùng
  // danh sách tuần riêng (xem publishableWeeks/dirtyWeeksData), không còn
  // phụ thuộc 3 biến này.
  const weekNumber = data?.week_number ?? isoWeekNumber(rangeFromISO);
  const weekEndISO = data?.week_end ?? addDaysISO(rangeFromISO, 6);
  const weekRange = formatWeekRange(data?.week_start ?? rangeFromISO, weekEndISO);

  // [1e] Nhãn thanh phạm vi — MỘT khuôn cho mọi N (wireframe §SC-009 [1e]):
  // "{dd/MM} – {dd/MM}/{yyyy} · {N} ngày". Dùng ĐÚNG rangeFromISO/rangeToISO
  // (khoảng người dùng thật sự đang chọn) — KHÔNG ưu tiên biên tuần đã
  // server-chuẩn-hoá (khác QuickEntry cũ từng làm — xem test đã viết lại):
  // với khoảng tuỳ ý không tròn tuần, biên tuần server RỘNG HƠN khoảng đang
  // thật sự hiển thị trên lưới.
  const viewDayCount =
    Math.round((parseISODate(rangeToISO).getTime() - parseISODate(rangeFromISO).getTime()) / 86400000) + 1;
  const rangeLabel = `${formatDayMonth(rangeFromISO)} – ${formatDateVi(rangeToISO)} · ${viewDayCount} ngày`;
  // Danh sách N ngày của khoảng đang xem — nguồn ĐỘC LẬP cho header lưới
  // (không phụ thuộc có nhân viên hay không, khác `visibleRows[0]?.days` cũ
  // vốn trống khi lưới rỗng). Nhãn thứ tự (T2..CN) tính client-side, KHÔNG
  // phụ thuộc `day_label` server (mỗi ngày trong mergedGroups vẫn giữ
  // `day_label` server riêng, dùng cho aria-label/ô lưới thật — chỉ header
  // cần nguồn độc lập này).
  const viewDates = useMemo(() => {
    const dates: { date: string; day_label: string }[] = [];
    for (let i = 0; i < viewDayCount; i += 1) {
      const iso = addDaysISO(rangeFromISO, i);
      const dow = parseISODate(iso).getDay(); // 0=CN..6=Thứ 7
      dates.push({ date: iso, day_label: WEEKDAY_LABELS_VI[dow === 0 ? 6 : dow - 1] });
    }
    return dates;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeFromISO, viewDayCount]);

  // [Nhập] (backend weekly-grid/import/* chỉ nhận `week_start` ĐƠN, không có
  // endpoint gộp nhiều tuần — xem header file, Rủi ro 3) dùng cờ này để biết
  // khoảng đang xem có khớp ĐÚNG một tuần máy chủ hay không, tránh áp dụng
  // NGẦM chỉ một phần khoảng đang hiển thị. [Xuất ▼] KHÔNG còn phụ thuộc cờ
  // này (Rủi ro 3 giải riêng phần đọc — xem comment handleExport). [Sao chép
  // lịch] (2026-09-14, tiếp #3) KHÔNG còn phụ thuộc cờ này nữa — 2 endpoint
  // mới `copy-range-preview`/`-commit` nhận khoảng ngày tuỳ ý, không cần khớp
  // đúng 1 tuần máy chủ (xem `chooseCopySource`/`isCopyTargetFullyPast`).
  const isSingleWeekView =
    touchedWeekStarts.length === 1 && !!data && rangeFromISO === data.week_start && rangeToISO === data.week_end;

  /** Tra tên ca từ danh mục mã ca (tooltip chip + aria-label ô) */
  const shiftNameOf = useMemo(() => {
    const codes = shiftCodesQuery.data?.items ?? [];
    return (shift: { shift_code: string | null; start_time: string; end_time: string; shift_label: string }) => {
      const tpl = codes.find(
        (item) =>
          (shift.shift_code != null && item.code === shift.shift_code) ||
          (item.start_time === shift.start_time && item.end_time === shift.end_time),
      );
      return tpl?.name ?? shift.shift_label;
    };
  }, [shiftCodesQuery.data]);

  // Gộp N tuần-máy-chủ (weekQueries) thành lưới N-ngày DUY NHẤT (2026-09-14,
  // thay THẲNG cho cặp `filteredGroups` đơn tuần / `rangeRows`+`rangeGroups`
  // chỉ-xem cũ) — cắt đúng ngày trong [rangeFromISO, rangeToISO] (kể cả 2
  // tuần biên không trọn vẹn), gộp theo work_area_id (KHÔNG theo tên — khác
  // `rangeGroups` cũ, tránh 2 khu vực trùng tên nhưng khác id bị gộp nhầm).
  // Mỗi ngày mang cờ `isPastWeek` mượn từ tuần-máy-chủ chứa nó (renderCell
  // dùng cờ này để khoá GHI đúng theo tuần chứa ngày, không theo cả khoảng —
  // xem MergedDayCell).
  const mergedGroups = useMemo<MergedGroup[]>(() => {
    if (rangeInvalid) return [];
    const byArea = new Map<string, MergedGroup>();
    weekQueries.forEach((q) => {
      const weekData = q.data;
      if (!weekData) return;
      weekData.groups.forEach((group) => {
        const key = group.work_area_id ?? "unassigned";
        let mergedGroup = byArea.get(key);
        if (!mergedGroup) {
          mergedGroup = {
            work_area_id: group.work_area_id,
            work_area_name: group.work_area_name,
            staff_count: group.staff_count,
            employees: [],
          };
          byArea.set(key, mergedGroup);
        }
        group.employees.forEach((emp) => {
          let row = mergedGroup!.employees.find((r) => r.user_id === emp.user_id);
          if (!row) {
            row = {
              user_id: emp.user_id,
              full_name: emp.full_name,
              employee_code: emp.employee_code,
              work_area_id: emp.work_area_id,
              work_area_name: emp.work_area_name,
              days: [],
              total_hours: 0,
              is_over_cap: false,
              transferred_from_branch_name: emp.transferred_from_branch_name,
            };
            mergedGroup!.employees.push(row);
          }
          row.is_over_cap = row.is_over_cap || emp.is_over_cap;
          emp.days
            .filter((d) => d.date >= rangeFromISO && d.date <= rangeToISO)
            .forEach((d) => row!.days.push({ ...d, isPastWeek: weekData.is_past_week }));
        });
      });
    });
    byArea.forEach((group) => {
      group.employees.forEach((row) => {
        row.days.sort((a, b) => a.date.localeCompare(b.date));
        row.total_hours = row.days.reduce(
          (sum, day) => sum + day.shifts.reduce((s, shift) => s + shift.hours, 0),
          0,
        );
      });
      group.employees.sort((a, b) => a.full_name.localeCompare(b.full_name, "vi"));
    });
    return Array.from(byArea.values());
  }, [weekQueries, rangeFromISO, rangeToISO, rangeInvalid]);

  // Ô đang mở popover [13] — đọc LIVE từ mergedGroups (đồng bộ sau mọi mutation)
  const activeCellData = useMemo(() => {
    if (!activeCell) return null;
    for (const group of mergedGroups) {
      for (const emp of group.employees) {
        if (emp.user_id !== activeCell.userId) continue;
        const day = emp.days.find((d) => d.date === activeCell.dateISO);
        if (day) return { employeeName: emp.full_name, day };
      }
    }
    return null;
  }, [activeCell, mergedGroups]);

  // Ô bị xoá khỏi lưới (đổi khu vực…) → đóng popover
  useEffect(() => {
    if (activeCell && mergedGroups.length > 0 && !activeCellData) setActiveCell(null);
  }, [activeCell, mergedGroups, activeCellData]);

  // Số NV điều chuyển trong lưới ĐANG MỞ (không áp bộ lọc khác) — dùng cho
  // nhãn "Chỉ NV điều chuyển (N)" + ẩn mục lọc khi N=0 (wireframe [11b]).
  const transferredCount = useMemo(
    () =>
      mergedGroups.reduce(
        (sum, g) => sum + g.employees.filter((emp) => emp.transferred_from_branch_name).length,
        0,
      ),
    [mergedGroups],
  );

  // Lưới sau khi áp bộ lọc nhân viên (S-MGR-FILTER 2026-08-09) — rỗng
  // selectedEmployeeIds = hiện tất cả (giữ nguyên hành vi cũ). Nhóm rỗng sau
  // lọc bị bỏ khỏi lưới (không hiện khu vực trống trơn). §SC-009 [11b]: thêm
  // transferredOnly (client-side, xem khai báo state) — kết hợp AND với bộ
  // lọc nhân viên nếu cả hai cùng bật. §SC-009 [11c]: thêm `searchMatchedEmployeeIds`
  // (vế 2 — chuỗi tìm trong tấm "Nhân viên") — kết hợp AND với cả hai vế trên
  // ("Hai vế kết hợp AND" — chọn NV cụ thể + chuỗi tìm — áp cho mọi độ dài
  // khoảng, không riêng gì "week").
  const filteredGroups = useMemo(() => {
    const selectedSet = selectedEmployeeIds.length > 0 ? new Set(selectedEmployeeIds) : null;
    if (!selectedSet && !transferredOnly && !searchMatchedEmployeeIds) return mergedGroups;
    return mergedGroups
      .map((group) => ({
        ...group,
        employees: group.employees.filter(
          (emp) =>
            (!selectedSet || selectedSet.has(emp.user_id)) &&
            (!transferredOnly || emp.transferred_from_branch_name) &&
            (!searchMatchedEmployeeIds || searchMatchedEmployeeIds.has(emp.user_id)),
        ),
      }))
      .filter((group) => group.employees.length > 0);
  }, [mergedGroups, selectedEmployeeIds, transferredOnly, searchMatchedEmployeeIds]);

  // BR-SCHD-011 (Nhập nhanh [5]): roster PHẲNG (không nhóm khu vực, không lọc)
  // của lưới ĐANG MỞ — "mọi dữ liệu tra cứu đã có sẵn phía trình duyệt từ
  // chính lưới đang mở" (không gọi API riêng). Dùng TOÀN BỘ roster tải được
  // (không áp selectedEmployeeIds — Nhập nhanh khớp theo văn bản gõ vào,
  // không theo bộ lọc VIEW-scope đang bật trên lưới).
  const quickEntryRoster = useMemo(() => {
    const byId = new Map<string, { user_id: string; full_name: string; employee_code: string | null }>();
    mergedGroups.forEach((group) => {
      group.employees.forEach((emp) => {
        if (!byId.has(emp.user_id)) {
          byId.set(emp.user_id, {
            user_id: emp.user_id,
            full_name: emp.full_name,
            employee_code: emp.employee_code,
          });
        }
      });
    });
    return Array.from(byId.values());
  }, [mergedGroups]);

  // Hàng NV hiển thị (nhóm đang mở) — phục vụ roving tabindex
  const visibleRows = useMemo(() => {
    const rows: MergedEmployeeRow[] = [];
    filteredGroups.forEach((group) => {
      const key = group.work_area_id ?? "unassigned";
      if (!collapsed[key]) rows.push(...group.employees);
    });
    return rows;
  }, [filteredGroups, collapsed]);

  // VĐ1 (2026-08-16, phương án 2): thả chuột Ở BẤT KỲ ĐÂU trên trang (không
  // chỉ trong ô lưới) → kết thúc kéo-chọn khoảng ngày, mở RangeAssignPopover
  // cho khoảng [min,max] đã chọn. visibleRows CÙNG thứ tự với rowIdx dùng khi
  // render (renderEmployeeRows tăng dần theo đúng nhóm đang mở, khớp
  // filteredGroups/collapsed nguồn của visibleRows) nên tra thẳng theo index.
  useEffect(() => {
    if (!isDragActive) return;
    const finishDrag = () => {
      setIsDragActive(false);
      setDragSelect((sel) => {
        if (!sel) return null;
        const emp = visibleRows[sel.rowIdx];
        if (emp) {
          const lo = Math.min(sel.startDayIdx, sel.endDayIdx);
          const hi = Math.max(sel.startDayIdx, sel.endDayIdx);
          // Rủi ro 2 (2026-09-14 tiếp): `lo`/`hi` là INDEX CỘT dùng chung cho
          // CẢ LƯỚI (khớp `dayIdx` truyền vào renderCell — xem đó), tra ngày
          // thật qua `viewDates` (nguồn chung, ĐỘC LẬP với hàng NV) — KHÔNG
          // tra qua `emp.days[lo]`/`emp.days[hi]` như trước: mảng `emp.days`
          // của MỘT hàng có thể NGẮN HƠN/lệch thứ tự so với các hàng khác khi
          // NV đó chỉ xuất hiện ở MỘT SỐ tuần-máy-chủ trong khoảng đang xem
          // (vd mới vào giữa khoảng) — index cột sẽ trỏ sai ngày cho đúng
          // NGAY hàng bắt đầu kéo-chọn (root cause Rủi ro 2, không chỉ ảnh
          // hưởng hàng khác).
          const startDate = viewDates[lo]?.date;
          const endDate = viewDates[hi]?.date;
          // 2026-09-07 (BR-SCHD-013): hàng ĐÃ lia qua — [rowLo,rowHi] — thành
          // danh sách user_id cho popover ở "chế độ vùng chọn" khi >1 NV.
          // rowLo === rowHi === sel.rowIdx (hành vi CŨ, 1 hàng) → mảng 1 phần
          // tử, targetEmployeeIds trong ShiftCellPopover fallback về [userId]
          // y hệt trước — KHÔNG regress trường hợp phổ biến nhất.
          const rowLo = Math.min(sel.rowIdx, sel.endRowIdx);
          const rowHi = Math.max(sel.rowIdx, sel.endRowIdx);
          const rangeRowsSlice = visibleRows.slice(rowLo, rowHi + 1);
          const rangeEmployeeIds = rangeRowsSlice.map((r) => r.user_id);
          // PO 2026-09-13: popover cần biết TÊN từng NV trong vùng kéo-chọn
          // (không chỉ id) để hiện danh sách trong banner phạm vi — snapshot
          // NGAY LÚC kéo-chọn, cùng nguồn `visibleRows` với rangeEmployeeIds.
          const rangeEmployeeNames = rangeRowsSlice.map((r) =>
            formatEmployeeLabel(r.employee_code, r.full_name),
          );
          if (startDate && endDate) {
            // Bổ sung 2026-08-17: mở CHÍNH ShiftCellPopover (không còn
            // RangeAssignPopover riêng) — endDateISO khác dateISO báo cho
            // popover biết đây là kéo-chọn khoảng ngày, tự set DateRangePicker
            // theo đúng khoảng đã kéo thay vì mặc định 1 ngày.
            setActiveCell({
              userId: sel.userId,
              dateISO: startDate,
              endDateISO: endDate,
              anchor: sel.anchorRect,
              rangeEmployeeIds,
              rangeEmployeeNames,
            });
          }
        }
        return null;
      });
    };
    window.addEventListener("mouseup", finishDrag);
    return () => window.removeEventListener("mouseup", finishDrag);
  }, [isDragActive, visibleRows]);

  // Employee tự đăng ký ca → Manager duyệt (2026-08-09) — gom ca PENDING theo
  // nhân viên trong TUẦN ĐẦU của khoảng đang xem (toàn bộ, KHÔNG áp bộ lọc
  // nhân viên/khu vực hiển thị — Manager cần thấy hết đề xuất chờ xử lý),
  // phục vụ nút "Duyệt tuần" hàng loạt ở pageHead. Giữ phạm vi 1-tuần CỐ Ý —
  // "Duyệt tuần" ngoài phạm vi việc gộp lưới N-ngày lần này (xem khai báo
  // `data`/weekNumber phía trên).
  const pendingByEmployee = useMemo(() => {
    const byId = new Map<string, { user_id: string; full_name: string; count: number }>();
    (data?.groups ?? []).forEach((group) => {
      group.employees.forEach((emp) => {
        let count = 0;
        emp.days.forEach((day) => {
          day.shifts.forEach((shift) => {
            if (shift.approval_status === "PENDING") count += 1;
          });
        });
        if (count > 0) byId.set(emp.user_id, { user_id: emp.user_id, full_name: emp.full_name, count });
      });
    });
    return Array.from(byId.values());
  }, [data]);
  const pendingTotal = pendingByEmployee.reduce((sum, e) => sum + e.count, 0);

  // ── Handlers ──

  /** [1a-range] `‹`/`›` — bước ±7 ngày GIỮ NGUYÊN độ dài khoảng (N ngày đang
   * chọn), KHÔNG mở lịch (wireframe §SC-009 [1a-range]). */
  const shiftRange = (deltaDays: number) => {
    if (blockedByUnpub()) return;
    setRangeFromISO((v) => addDaysISO(v, deltaDays));
    setRangeToISO((v) => addDaysISO(v, deltaDays));
  };

  /** [Tuần này] — phím tắt reset về ĐÚNG 7 ngày của tuần lịch hiện tại, bất
   * kể lựa chọn trước đó (wireframe §SC-009 [1a-range]: "là phím tắt, không
   * phải ràng buộc lên cơ chế chọn ngày tự do"). */
  const resetToCurrentWeek = () => {
    if (blockedByUnpub()) return;
    setRangeFromISO(defaultRangeFromISO);
    setRangeToISO(defaultRangeToISO);
  };

  /** [CAL] — áp dụng khoảng/ngày chọn từ ContinuousScrollDatePicker. */
  const applyCalSelection = (value: DateFilterValue) => {
    if (blockedByUnpub()) return;
    if (value.mode === "single") {
      setRangeFromISO(value.date);
      setRangeToISO(value.date);
    } else {
      setRangeFromISO(value.from);
      setRangeToISO(value.to);
    }
  };

  const toggleGroup = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const openCellPopover = (
    emp: MergedEmployeeRow,
    day: MergedDayCell,
    target: HTMLButtonElement,
  ) => {
    if (day.isPastWeek) return; // tuần-máy-chủ chứa ngày này đã qua → read-only (biến thể C)
    const r = target.getBoundingClientRect();
    setActiveCell({
      userId: emp.user_id,
      dateISO: day.date,
      anchor: { left: r.left, top: r.top, bottom: r.bottom, right: r.right },
    });
  };

  /** Roving tabindex: ←→↑↓ di chuyển focus giữa các ô; Enter/Space kích hoạt nút.
   * Biên cột ĐỘNG theo N ngày đang hiển thị (trước đây cứng 6 = 7 cột "Tuần"). */
  const handleCellKeyDown = (e: React.KeyboardEvent, r: number, c: number) => {
    const maxCol = Math.max(0, viewDayCount - 1);
    const moves: Record<string, { r: number; c: number }> = {
      ArrowLeft: { r, c: Math.max(0, c - 1) },
      ArrowRight: { r, c: Math.min(maxCol, c + 1) },
      ArrowUp: { r: Math.max(0, r - 1), c },
      ArrowDown: { r: Math.min(visibleRows.length - 1, r + 1), c },
    };
    const next = moves[e.key];
    if (!next) return;
    e.preventDefault();
    setFocusPos(next);
    const el = document.querySelector<HTMLButtonElement>(
      `[data-cell-pos="${next.r}:${next.c}"]`,
    );
    el?.focus();
  };

  // [Sao chép lịch] khoảng ngày tuỳ ý (2026-09-14, tiếp #3 — generalize
  // handleCopyPreviousWeek cũ, đã XOÁ: chỉ chép ĐÚNG 1 tuần→1 tuần, không còn
  // caller nào khác sau khi đổi nút thành menu). Khoảng ĐÍCH LUÔN = khoảng
  // đang lọc trên toolbar (`rangeFromISO`/`rangeToISO`) — "một nơi quyết định
  // phạm vi", modal KHÔNG cho sửa đích. 3 lựa chọn menu chỉ khác NGUỒN MẶC
  // ĐỊNH — người dùng sửa lại được ngay trong modal trước khi bấm "Sao chép".
  //
  // Nguồn mặc định — port ĐÚNG prototype `openCopyMenu` (sc009-build-prototype.html
  // dòng ~2670-2682), KHÔNG suy diễn riêng:
  //   "week"  = 7 ngày liền trước rangeFromISO, CÙNG ĐỘ DÀI với khoảng đích
  //             đang xem (viewDayCount) — không cố định 7 ngày.
  //   "month" = 30 ngày trước rangeFromISO, CŨNG cùng độ dài đích (không phải
  //             1 khối 30-ngày cố định — khớp `rangeLenDays()` của prototype).
  //   "range" = mặc định TẠM 7 ngày liền trước (khác 2 lựa chọn trên: LUÔN
  //             đúng 7 ngày bất kể độ dài đích) — người dùng tự chọn lại ngay
  //             trong modal, đây chỉ là điểm khởi đầu.
  const chooseCopySource = (mode: "week" | "month" | "range") => {
    setOpenMenu(null);
    let from: string;
    let to: string;
    if (mode === "week") {
      from = addDaysISO(rangeFromISO, -7);
      to = addDaysISO(rangeFromISO, -7 + viewDayCount - 1);
    } else if (mode === "month") {
      from = addDaysISO(rangeFromISO, -30);
      to = addDaysISO(from, viewDayCount - 1);
    } else {
      from = addDaysISO(rangeFromISO, -7);
      to = addDaysISO(rangeFromISO, -1);
    }
    setCopyModalSource({ from, to });
  };

  /** Bấm nút toolbar [Sao chép lịch] — mở menu 3 lựa chọn, TRỪ khi khoảng
   * đích đang xem đã qua hẳn (`isCopyTargetFullyPast`): nút vẫn CLICKABLE
   * (mờ trực quan qua `styles.btnLocked`, không `disabled`) nên phải tự chặn
   * + báo toast ở đây — đúng hành vi prototype `openCopyMenu`/`#btnCopy`
   * ("đã ẩn/mờ ở toolbar, chặn thêm ở đây" + toast "Đổi khoảng sang hôm
   * nay/tương lai trước khi sao chép."). */
  const openCopyMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isCopyTargetFullyPast) {
      setToast("Đổi khoảng sang hôm nay/tương lai trước khi sao chép.");
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
    setOpenMenu(openMenu === "copy" ? null : "copy");
  };

  // "Xuất ▼" — backend (`weekly-grid/export`) chỉ nhận `week_start` ĐƠN,
  // KHÔNG có endpoint gộp nhiều tuần. Rủi ro 3 (2026-09-14 tiếp) GIẢI cho
  // riêng XUẤT (khác NHẬP — xem comment header file): đây là thao tác CHỈ
  // ĐỌC (tải file), nên LẶP TUẦN TỰ gọi lại API đơn-tuần hiện có cho từng
  // tuần-máy-chủ trong `touchedWeekStarts` (cùng nguyên tắc "lặp tuần tự" đã
  // dùng ở confirmPublish/confirmDiscardChanges) — mỗi tuần TẢI FILE RIÊNG
  // (backend đã đặt tên file kèm `week_start`, xem Content-Disposition), thay
  // vì tự GHÉP nội dung 2 file thành 1: `.xlsx` là archive OOXML nhị phân,
  // ghép chuỗi thô sẽ RA FILE HỎNG (cần thư viện xử lý spreadsheet mới làm
  // đúng — ngoài phạm vi, cần @architect); `.csv` ghép được bằng text nhưng
  // tự áp dụng cách xử lý RIÊNG cho từng định dạng sẽ không nhất quán và tự
  // suy ra ngữ nghĩa gộp (giữ header nào, thứ tự dòng…) chưa có trong
  // wireframe. "Tải N file" đã đủ AN TOÀN + ĐƠN GIẢN + nhất quán 2 định dạng —
  // không đọc/diễn giải nội dung file ở FE.
  const handleExport = async (format: "csv" | "xlsx") => {
    if (rangeInvalid || touchedWeekStarts.length === 0) return;
    setOpenMenu(null);
    const kind = format === "xlsx" ? "Excel (.xlsx)" : "CSV (.csv)";
    const isMultiWeek = touchedWeekStarts.length > 1;
    setToast(isMultiWeek ? `Đang xuất ${kind} (${touchedWeekStarts.length} tuần)…` : `Đang xuất ${kind}…`);
    const failed: { weekStart: string; message: string }[] = [];
    let okCount = 0;
    // Tuần tự (không Promise.all) — tránh bắn N tải file cùng lúc (trình
    // duyệt/OS có thể chặn tải hàng loạt đồng thời không do 1 cú click trực
    // tiếp gây ra) và giữ đúng thứ tự tuần trong danh sách lỗi báo cáo.
    for (const weekStart of touchedWeekStarts) {
      try {
        const file = await managerApi.exportWeeklyGrid(weekStart, format);
        const url = URL.createObjectURL(file.blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        okCount += 1;
      } catch (error) {
        failed.push({ weekStart, message: extractApiError(error).message });
      }
    }
    if (!isMultiWeek) {
      // Đúng 1 tuần — GIỮ NGUYÊN hành vi đơn-tuần cũ (SPEC-GAP: spec chỉ có
      // toast LỖI, không toast thành công) để không đổi trải nghiệm ở trường
      // hợp phổ biến nhất.
      if (failed.length > 0) setToast("Xuất không thành công. Thử lại");
      else setToast(null);
      return;
    }
    setToast(
      failed.length === 0
        ? `Đã xuất ${okCount}/${touchedWeekStarts.length} tuần.`
        : `Đã xuất ${okCount}/${touchedWeekStarts.length} tuần — lỗi: ${failed
            .map((f) => `${formatWeekRange(f.weekStart, addDaysISO(f.weekStart, 6))} (${f.message})`)
            .join("; ")}`,
    );
  };

  // Employee tự đăng ký ca → Manager duyệt (2026-08-09) — [Duyệt tuần] hàng
  // loạt, giữ phạm vi TUẦN ĐẦU của khoảng đang xem CỐ Ý (xem pendingByEmployee
  // phía trên) — gửi neo THÔ `rangeFromISO`, backend tự floor (cùng pattern
  // ISS-PILOT-16 cũ).
  const handleApproveWeekFor = (userId: string) => {
    // bot-037 (GH#803): chặn duyệt tuần đã khoá — banner [20] đã hiện đúng
    // nhưng nút này trước đây không kiểm, ghi thẳng giờ vào tuần quá khứ.
    if (weekApprovalMutation.isPending || isApprovalTargetWeekLocked) return;
    weekApprovalMutation.mutate(
      { tenant_user_id: userId, week_start: rangeFromISO, approved: true },
      {
        onSuccess: (result) => setToast(`Đã duyệt ${result.updated_count} ca đề xuất.`),
        onError: (error) => setToast(extractApiError(error).message),
      },
    );
  };

  const startWeekReject = (userId: string) => {
    setWeekRejectingUserId(userId);
    setWeekRejectReason("");
  };

  const confirmWeekReject = () => {
    // bot-037 (GH#803): cùng khoá với duyệt — tuần đã qua "chỉ xem", không
    // duyệt lẫn từ chối hàng loạt.
    if (
      weekApprovalMutation.isPending ||
      isApprovalTargetWeekLocked ||
      !weekRejectingUserId ||
      !weekRejectReason.trim()
    )
      return;
    weekApprovalMutation.mutate(
      {
        tenant_user_id: weekRejectingUserId,
        week_start: rangeFromISO,
        approved: false,
        reason: weekRejectReason.trim(),
      },
      {
        onSuccess: (result) => {
          setToast(`Đã từ chối ${result.updated_count} ca đề xuất.`);
          setWeekRejectingUserId(null);
          setWeekRejectReason("");
        },
        onError: (error) => setToast(extractApiError(error).message),
      },
    );
  };

  // Tuần CÓ THỂ xuất bản trong khoảng đang xem — loại tuần quá khứ hẳn
  // (backend publish_week() luôn 422 khi `sunday < today`) và tuần rỗng
  // (backend luôn 422 "Tuần chưa có ca") KHỎI danh sách "sẽ xuất bản" — tránh
  // gọi vào chỗ CHẮC CHẮN lỗi, cùng tinh thần D-7 has_restorable_snapshot ở
  // trên. Dialog xác nhận liệt kê ĐÚNG danh sách này (wireframe [PUB]
  // "dialog nêu rõ các tuần sẽ được xuất bản").
  const publishableWeeks = useMemo(
    () => weeksData.filter((w) => !w.is_past_week && w.status !== "empty"),
    [weeksData],
  );
  const skippedPublishWeeks = useMemo(
    () => weeksData.filter((w) => w.is_past_week || w.status === "empty"),
    [weeksData],
  );

  // [4] Banner cảnh báo tổng hợp — CỘNG warnings_count qua mọi tuần-máy-chủ
  // chạm khoảng đang xem, nối warnings_summary từng tuần thành 1 chuỗi (đơn
  // giản hơn dựng lại câu văn — mỗi summary server đã tự đủ nghĩa "N cảnh
  // báo: ...", nối bằng " · " đọc vẫn rõ khi >1 tuần).
  const warningsCount = weeksData.reduce((sum, w) => sum + w.warnings_count, 0);
  const warningsSummaryText = weeksData
    .filter((w) => w.warnings_summary)
    .map((w) => (weeksData.length > 1 ? `Tuần ${formatWeekRange(w.week_start, w.week_end)}: ${w.warnings_summary}` : w.warnings_summary))
    .join(" · ") || null;

  /** [12] → [14b] (còn vi phạm, mode Chặn) · [15] (≥N ngày) · [16] công-bố-muộn
   * (<N ngày) theo cấu hình SC-005.
   *
   * Nhiều tuần trong khoảng đang xem (wireframe [PUB] "Phạm vi xuất bản khi
   * khoảng không trùng tuần lịch"): đơn vị xuất bản máy chủ VẪN LÀ TUẦN LỊCH
   * — thao tác áp cho TOÀN BỘ `publishableWeeks`. `confirmPublish` LẶP TUẦN
   * TỰ qua `publishMutation` (mỗi lệnh gọi vẫn atomic/idempotent Y HỆT
   * single-week — `publish_week()` backend KHÔNG đổi) — không có giao dịch
   * xuyên-tuần nào cần backend mới; kết quả từng tuần báo rõ sau vòng lặp
   * (không "ghi nửa vời" mập mờ — xem confirmPublish).
   *
   * Nút [12] KHÔNG dùng `disabled` NATIVE khi `blockModeHasViolations` — native
   * `disabled` chặn HOÀN TOÀN sự kiện click ở trình duyệt, `onClick` không bao
   * giờ chạy, phản hồi duy nhất còn lại là `title` (tooltip hover-only, vô
   * dụng trên mobile/touch). Nút phải LUÔN nhận click (chỉ khoá TRỰC QUAN qua
   * styles.btnLocked khi còn vi phạm mode Chặn) vì openPublishDialog rẽ nhánh
   * ngay tại đây để hiện rõ danh sách vi phạm thay vì mở luôn dialog xác nhận
   * xuất bản. */
  const openPublishDialog = () => {
    if (publishableWeeks.length === 0) return;
    setPublishError(null);
    if (blockModeHasViolations) {
      setPublishDialog("blocked");
      return;
    }
    const msPerDay = 86400000;
    const todayStart = parseISODate(toISODate(new Date())).getTime();
    // Bảo thủ: tuần SỚM NHẤT trong danh sách sẽ xuất bản quyết định nhánh
    // "late"/"normal" — 1 tuần trễ hẹn cũng đủ để cảnh báo trước khi xuất
    // bản, không đợi tới tuần trễ nhất.
    const earliestWeek = publishableWeeks[0];
    const daysAhead = Math.ceil((parseISODate(earliestWeek.week_start).getTime() - todayStart) / msPerDay);
    setPublishDialog(daysAhead < earliestWeek.rules.publish_ahead_days ? "late" : "normal");
  };

  const confirmPublish = async () => {
    if (isPublishingBatch || publishableWeeks.length === 0) return;
    setIsPublishingBatch(true);
    const results: { week: WeeklyGridData; ok: boolean; message?: string }[] = [];
    for (const week of publishableWeeks) {
      try {
        await publishMutation.mutateAsync(week.week_start);
        results.push({ week, ok: true });
      } catch (error) {
        results.push({ week, ok: false, message: extractApiError(error).message });
      }
    }
    setIsPublishingBatch(false);
    setPublishDialog(null);
    setPublishError(null);
    const okCount = results.filter((r) => r.ok).length;
    if (okCount > 0) setPublishNotice(true); // [21]
    if (results.length === 1) {
      // Đúng 1 tuần — GIỮ NGUYÊN khuôn thông báo đơn-tuần cũ (dùng
      // `week_number` server, không phải khoảng ngày; lỗi hiện NGUYÊN VĂN
      // message backend, không thêm tiền tố) để không đổi trải nghiệm ở
      // trường hợp phổ biến nhất (N=1, vẫn chiếm đa số lượt dùng).
      const only = results[0];
      setToast(
        only.ok
          ? `Đã xuất bản lịch tuần ${only.week.week_number} · ${formatWeekRange(only.week.week_start, only.week.week_end)} tới nhân viên`
          : (only.message ?? "Xuất bản không thành công."),
      );
    } else {
      const failed = results.filter((r) => !r.ok);
      setToast(
        failed.length === 0
          ? `Đã xuất bản ${okCount}/${results.length} tuần tới nhân viên.`
          : `Đã xuất bản ${okCount}/${results.length} tuần — bỏ qua: ${failed
              .map((r) => `${formatWeekRange(r.week.week_start, r.week.week_end)} (${r.message})`)
              .join("; ")}`,
      );
    }
  };

  /** Popup [18] [Hủy thay đổi] — khôi phục về bản xuất bản gần nhất (backend
   * discard_week_changes: sửa quay lại giá trị cũ, xoá được khôi phục, thêm
   * sau-publish bị huỷ). invalidateGrid trong useDiscardScheduleWeekChanges tự
   * refetch — dirty_after_publish trả về false sẽ tự ẩn popup này. Nhiều tuần
   * dirty trong khoảng đang xem → LẶP TUẦN TỰ qua `dirtyWeeksData` (cùng
   * nguyên tắc atomic-mỗi-tuần với confirmPublish ở trên). */
  const confirmDiscardChanges = async () => {
    if (isDiscardingBatch || !canRestoreSnapshot || dirtyWeeksData.length === 0) return;
    setIsDiscardingBatch(true);
    const results: { week: WeeklyGridData; ok: boolean; message?: string }[] = [];
    for (const week of dirtyWeeksData) {
      try {
        await discardChangesMutation.mutateAsync(week.week_start);
        results.push({ week, ok: true });
      } catch (error) {
        results.push({ week, ok: false, message: extractApiError(error).message });
      }
    }
    setIsDiscardingBatch(false);
    const okCount = results.filter((r) => r.ok).length;
    const failed = results.filter((r) => !r.ok);
    if (results.length === 1) {
      // Đúng 1 tuần — GIỮ NGUYÊN khuôn thông báo đơn-tuần cũ (cùng lý do đã
      // nêu ở confirmPublish phía trên).
      const only = results[0];
      setToast(
        only.ok
          ? `Đã hủy thay đổi — lịch tuần ${only.week.week_number} trở về đúng bản đã xuất bản`
          : (only.message ?? "Hủy thay đổi không thành công."),
      );
    } else {
      setToast(
        failed.length === 0
          ? `Đã hủy thay đổi ${okCount}/${results.length} tuần — trở về đúng bản đã xuất bản.`
          : `Đã hủy thay đổi ${okCount}/${results.length} tuần — bỏ qua: ${failed
              .map((r) => `${formatWeekRange(r.week.week_start, r.week.week_end)} (${r.message})`)
              .join("; ")}`,
      );
    }
  };

  // Nút [12] — HARD disabled (native `disabled`, đúng nghĩa "không có gì để làm"):
  // chưa có dữ liệu · không tuần nào trong khoảng đang xem có thể xuất bản ·
  // mọi tuần có thể xuất bản đều đã "Đã xuất bản" & sạch. Mode Chặn còn vi
  // phạm (D-6) KHÔNG nằm trong danh sách này — xem lý do ở openPublishDialog
  // phía trên, nút vẫn phải nhận click để mở dialog vi phạm [14b].
  const blockModeHasViolations = publishableWeeks.some(
    (w) => w.rules.violation_mode === "block" && w.warnings_count > 0,
  );
  const publishHardDisabled =
    weeksData.length === 0 ||
    publishableWeeks.length === 0 ||
    publishableWeeks.every((w) => w.status === "published" && !w.dirty_after_publish);
  // Khoá TRỰC QUAN (opacity mờ qua styles.btnLocked) nhưng KHÔNG dùng `disabled`/
  // `aria-disabled` — chú ý: .btn[aria-disabled="true"] trong CSS module này tự
  // gán pointer-events:none, nên đặt aria-disabled ở đây sẽ tái tạo lại đúng cái
  // bug đang sửa. Nút vẫn hoàn toàn clickable + focusable + đọc được bằng SR.
  const publishVisuallyLocked = !publishHardDisabled && blockModeHasViolations;
  const publishTitle = blockModeHasViolations
    ? "Lịch tuần còn vi phạm ràng buộc (Chặn) — bấm để xem chi tiết vi phạm."
    : undefined;

  // Nhãn/title nút bị khoá bởi guard D-6
  const guardProps = guardLocked
    ? { disabled: true, title: "Có thay đổi chưa công bố — nhấn [Xuất bản lại] trước" }
    : {};

  const publishDaysAhead = publishableWeeks[0]
    ? Math.ceil(
        (parseISODate(publishableWeeks[0].week_start).getTime() -
          parseISODate(toISODate(new Date())).getTime()) /
          86400000,
      )
    : 0;

  // ── Render helpers ──

  /** [8]/[8b] Badge trạng thái: ● Chưa xuất bản → ✔ Đã xuất bản → badge KÉP khi dirty */
  const renderBadge = () => {
    // ISS-MGR-1 REOPEN (2026-08-10, Hang Le): chỉ còn ĐÚNG 2 nhãn — "Chưa xuất bản" /
    // "Đã xuất bản". Gộp lại 2 nhánh cũ (isEmptyWeek "○ Chưa xếp" thêm ở bản fix 2026-08-09
    // + !isPublished "● Nháp") vì cả 2 đều cùng một sự thật "chưa công bố" — tên gọi khác
    // nhau chỉ gây rối, không phải 2 trạng thái người dùng cần phân biệt. Phân tích đầy đủ:
    // PROPOSAL-SC-009-schedule-audit-panel-2026-08-10.md §3.3.1 — "Chưa xuất bản" không mang
    // hàm ý "đang sửa dở" như "Nháp" nên đúng cho cả tuần rỗng lẫn tuần có nháp; badge thứ 3
    // (màu xám statusBadgeDual) trở nên thừa, gộp về style statusBadgeDraft (giữ chấm tròn).
    // isEmptyWeek vẫn giữ nguyên cho banner/nút xuất bản/nhãn ngày rỗng — CHỈ badge đổi.
    if (!isPublished) {
      return (
        <span className={`${styles.statusBadge} ${styles.statusBadgeDraft}`}>
          <span className={styles.statusBadgeDot} aria-hidden="true" />
          Chưa xuất bản
        </span>
      );
    }
    if (guardLocked) {
      return (
        <span className={`${styles.statusBadge} ${styles.statusBadgeDual}`}>
          <CheckIcon />
          Đã xuất bản ·{" "}
          <span className={styles.statusBadgeDot} aria-hidden="true" />
          có thay đổi chưa công bố
        </span>
      );
    }
    return (
      <span className={`${styles.statusBadge} ${styles.statusBadgePublished}`}>
        <CheckIcon />
        Đã xuất bản
      </span>
    );
  };

  /** Ô lưới [5]: chip mã ca xếp DỌC · ô trống "—" hover "+" · ⚠ is-violation
   *
   * VĐ1 (2026-08-16, phương án 2): ô TRỐNG bấm-giữ rồi lia chuột sang ô ngày
   * khác CÙNG HÀNG để chọn khoảng ngày → thả chuột mở RangeAssignPopover
   * (gán mã ca cho cả khoảng, có thể điều động chi nhánh). Ô ĐÃ CÓ ca giữ
   * NGUYÊN hành vi cũ — click mở ShiftCellPopover xem/sửa/xoá ca, KHÔNG đổi.
   */
  const renderCell = (emp: MergedEmployeeRow, dayIdx: number, rowIdx: number) => {
    // Rủi ro 2 (2026-09-14 tiếp): định vị theo NGÀY THẬT (`viewDates[dayIdx].date`
    // — cột dùng CHUNG cho mọi hàng, xem khai báo `viewDates`) rồi tra `emp.days`
    // BẰNG KHOÁ (`date`), thay vì `emp.days[dayIdx]` theo VỊ TRÍ mảng riêng của
    // từng hàng như trước. `emp.days` của 1 NV có thể NGẮN HƠN/khác thứ tự
    // `viewDates` khi NV đó chỉ xuất hiện ở MỘT SỐ tuần-máy-chủ trong khoảng
    // đang xem (vd mới vào giữa khoảng, hoặc trước Rủi ro 1 — nay đã sửa —
    // touchedWeekStarts bỏ sót tuần) — index-based sẽ đặt sai ngày dưới đúng
    // cột header (renderGrid giờ LUÔN lặp theo `viewDates`, xem đó). NV không
    // có dữ liệu ngày này → coi là ô CHỈ-XEM (isPastWeek:true): không suy ra
    // được ngữ cảnh roster/tuần-máy-chủ nào phủ ngày đó cho NV này nên KHOÁ
    // ghi thay vì đoán (an toàn hơn để lộ chip trống có thể bấm được).
    const targetDate = viewDates[dayIdx]?.date ?? "";
    const day: MergedDayCell = emp.days.find((d) => d.date === targetDate) ?? {
      date: targetDate,
      day_label: viewDates[dayIdx]?.day_label ?? "",
      shifts: [],
      warnings: [],
      isPastWeek: true,
    };
    // R42 "chặn GHI theo NGÀY" (wireframe §SC-009 [R42]) — ở granularity
    // backend thật sự cung cấp: khoá theo tuần-máy-chủ CHỨA ngày này, không
    // theo cả khoảng đang xem (isPastWeek trang chỉ còn dùng cho banner [20]
    // khi TOÀN BỘ khoảng đã qua — xem khai báo `isPastWeek` phía trên).
    const dayIsPastWeek = day.isPastWeek;
    const hasShifts = day.shifts.length > 0;
    const isViolation = day.warnings.length > 0;
    // Phân loại nhanh theo nội dung message backend trả (không đổi schema):
    // "Vượt trần..." = đỏ (nghiêm trọng hơn), còn lại (Nghỉ giữa 2 ca) = vàng.
    const isCapViolation = day.warnings.some((w) => w.includes("Vượt trần"));
    const violationTooltip = isCapViolation ? "Vượt trần giờ làm" : "Khoảng nghỉ giữa 2 ca không đủ";
    const names = day.shifts.map((s) => shiftNameOf(s)).join(", ");
    const emptyLabel = isEmptyWeek ? "chưa xếp ca" : "Nghỉ";
    // Ý 4 (2026-08-16, ĐÍNH CHÍNH): ban đầu khoá click cho ô điều động — theo
    // phản hồi người dùng, MANAGER chi nhánh gốc vẫn phải sửa/xoá được ca họ
    // tự điều động (backend đã bỏ chặn tương ứng). CHỈ còn dùng cờ này để
    // hiện badge chi nhánh trên chip, KHÔNG còn khoá tương tác.
    const crossBranchShift = day.shifts.find((s) => s.is_cross_branch);
    const isCrossBranchCell = !!crossBranchShift;
    const ariaLabel = `${emp.full_name}, ${day.day_label} ${formatDayMonth(day.date)}: ${
      hasShifts ? names : emptyLabel
    }${isViolation ? " — vi phạm nghỉ giữa 2 ca" : ""}${
      isCrossBranchCell
        ? ` — điều động sang ${crossBranchShift!.branch_name ?? "chi nhánh khác"}`
        : ""
    }`;
    const tabIndex = focusPos.r === rowIdx && focusPos.c === dayIdx ? 0 : -1;
    // 2026-09-07 (BR-SCHD-013, wireframe [2b] "lia theo CẢ HAI CHIỀU"): trước
    // đây chỉ so `dragSelect.rowIdx === rowIdx` (1 hàng duy nhất) — nay so
    // KHOẢNG hàng [rowIdx, endRowIdx] để tô chọn đúng hình chữ nhật khi lia
    // dọc qua nhiều nhân viên. 1 hàng (hành vi CŨ) là trường hợp riêng khi
    // rowIdx === endRowIdx.
    const isDragSelected =
      !!dragSelect &&
      rowIdx >= Math.min(dragSelect.rowIdx, dragSelect.endRowIdx) &&
      rowIdx <= Math.max(dragSelect.rowIdx, dragSelect.endRowIdx) &&
      dayIdx >= Math.min(dragSelect.startDayIdx, dragSelect.endDayIdx) &&
      dayIdx <= Math.max(dragSelect.startDayIdx, dragSelect.endDayIdx);

    return (
      <td key={day.date} role="gridcell">
        <button
          type="button"
          data-cell-pos={`${rowIdx}:${dayIdx}`}
          className={`${styles.shiftCell} ${!hasShifts ? styles.shiftCellEmpty : ""} ${
            isViolation ? (isCapViolation ? styles.shiftCellViolationCap : styles.shiftCellViolation) : ""
          } ${dayIsPastWeek ? styles.shiftCellReadonly : ""} ${
            isDragSelected ? styles.shiftCellDragSelected : ""
          }`}
          aria-label={ariaLabel}
          tabIndex={dayIsPastWeek ? -1 : tabIndex}
          onMouseDown={(e) => {
            if (dayIsPastWeek || hasShifts) return; // ô đã có ca: giữ hành vi click cũ (xem bên dưới)
            e.preventDefault();
            setFocusPos({ r: rowIdx, c: dayIdx });
            const rect = e.currentTarget.getBoundingClientRect();
            setIsDragActive(true);
            setDragSelect({
              rowIdx,
              endRowIdx: rowIdx,
              userId: emp.user_id,
              fullName: emp.full_name,
              startDayIdx: dayIdx,
              endDayIdx: dayIdx,
              anchorRect: { left: rect.left, top: rect.top, bottom: rect.bottom, right: rect.right },
            });
          }}
          onMouseEnter={() => {
            // 2026-09-07 (BR-SCHD-013): BỎ chặn `dragSelect.rowIdx !== rowIdx`
            // — cho phép lia chuột DỌC qua nhiều hàng nhân viên (2 chiều), cập
            // nhật CẢ `endRowIdx` lẫn `endDayIdx` mỗi lần con trỏ vào 1 ô mới.
            if (!isDragActive || !dragSelect) return;
            setDragSelect((prev) => (prev ? { ...prev, endRowIdx: rowIdx, endDayIdx: dayIdx } : prev));
          }}
          onClick={(e) => {
            if (hasShifts) {
              // Hành vi CŨ, KHÔNG đổi: click ô đã có ca → mở ShiftCellPopover
              // (kể cả ca điều động — chi nhánh gốc vẫn sửa/xoá được, xem
              // đính chính ở comment isCrossBranchCell phía trên).
              setFocusPos({ r: rowIdx, c: dayIdx });
              openCellPopover(emp, day, e.currentTarget);
            }
            // Ô trống: KHÔNG mở gì ở onClick — xử lý qua mousedown/mouseup
            // (kéo-chọn) ở trên/window listener, kể cả khi chỉ bấm-thả tại
            // chỗ (không lia chuột) — vẫn coi là chọn 1 ngày duy nhất.
          }}
          onKeyDown={(e) => !dayIsPastWeek && handleCellKeyDown(e, rowIdx, dayIdx)}
        >
          {isViolation && (
            <PortalTooltip
              text={violationTooltip}
              className={isCapViolation ? styles.shiftCellWarnCap : styles.shiftCellWarn}
              ariaLabel={violationTooltip}
            >
              <WarnIcon size={13} />
            </PortalTooltip>
          )}
          {hasShifts ? (
            // 2026-08-11 (theo yêu cầu user): chip chỉ hiện MÃ CA, bỏ dòng giờ rút gọn
            // bên dưới — gọn hơn, giờ ca xem trong popover khi bấm vào ô.
            day.shifts.map((shift) => (
              <span
                key={shift.schedule_id}
                className={`${styles.shiftChip} ${
                  // Ca ngày nghỉ → chip xám "Ca nghỉ"; khối "Phân theo giờ" →
                  // màu theo contentChipClass (OT vs. giờ thường, xem [OT]).
                  shift.is_day_off ? styles.shiftChipDayOff : contentChipClass(shift)
                } ${approvalChipClass(shift.approval_status)} ${
                  shift.is_cross_branch ? styles.shiftChipCrossBranch : ""
                } ${startedChipClass(day.date, shift.start_time, shift.approval_status)}`}
              >
                {/* 2026-08-18 (theo yêu cầu người dùng, khôi phục thiết kế gốc
                    2026-08-16 bị thu hẹp nhầm lúc gộp #630): hiện tên/mã chi
                    nhánh trên MỌI chip đã gán ca, không chỉ riêng ca điều
                    động — is_cross_branch chỉ còn dùng để tô màu cảnh báo.
                    Nằm TRONG dòng flex-column của chip (không absolute) để
                    không đè lên mã ca. */}
                {(shift.branch_code || shift.branch_name) && (
                  <span
                    className={`${styles.shiftChipBranch} ${
                      shift.is_cross_branch ? styles.shiftChipBranchAlert : ""
                    }`}
                  >
                    {shift.branch_code ?? shift.branch_name}
                  </span>
                )}
                <span className={styles.shiftChipCode}>
                  {shift.is_day_off ? "Ca nghỉ" : (shift.shift_code ?? shift.shift_label)}
                  {/* [OT]: "· OT" chỉ cho khối giờ tuỳ chỉnh tick OT (không
                      áp cho ca có mã ca cố định) */}
                  {!shift.is_day_off && !shift.shift_code && shift.is_overtime ? " · OT" : ""}
                </span>
                {/* wireframe [1-chip]: "Ca do nhân viên tự đề xuất thêm dòng
                    thứ ba 'Chờ duyệt'/'Hết hạn'" — trước đây màn lưới chính
                    thiếu dòng này (chỉ thẻ mobile có), xem [EXP]. */}
                {shift.approval_status === "PENDING" && (
                  <span className={styles.shiftChipSub}>Chờ duyệt</span>
                )}
                {shift.approval_status === "EXPIRED" && (
                  <span className={styles.shiftChipSub}>Hết hạn</span>
                )}
              </span>
            ))
          ) : (
            <span className={styles.shiftCellDash} aria-hidden="true" />
          )}
        </button>
      </td>
    );
  };

  const gridAriaLabel = `Bảng phân ca ${rangeLabel}${
    isPastWeek ? " (chỉ đọc)" : isEmptyWeek ? " (chưa xếp)" : ""
  }`;

  const renderGrid = () => {
    let rowIdx = -1;
    // ISS-MGR-6 (2026-08-09): tách render dòng NV ra hàm dùng chung — port
    // nguyên pattern từ ManagerAttendancePage.tsx (renderGroupRows) để nhóm
    // "Chưa phân khu vực" tái dùng cho cả nhánh có header lẫn nhánh không.
    const renderEmployeeRows = (employees: MergedEmployeeRow[]) =>
      employees.map((emp) => {
        rowIdx += 1;
        const currentRow = rowIdx;
        return (
          <tr key={emp.user_id} role="row">
            <th className={styles.colEmp} scope="row" role="rowheader">
              {/* ISS-MGR-14 (2026-08-09, @designer chốt): bỏ tooltip tên NV —
                  cột "Nhân viên" wrap 2 dòng thay vì truncate+tooltip. */}
              <span className={styles.empName}>
                {formatEmployeeLabel(emp.employee_code, emp.full_name)}
                {emp.transferred_from_branch_name && (
                  <span className={styles.empTransfer}>
                    điều chuyển từ {emp.transferred_from_branch_name}
                  </span>
                )}
              </span>
            </th>
            {/* Rủi ro 2 (2026-09-14 tiếp): lặp theo `viewDates` (số cột CỐ ĐỊNH,
                dùng chung mọi hàng) thay vì `emp.days` (trước đây — độ dài mảng
                RIÊNG từng hàng, có thể ngắn hơn khi NV chỉ xuất hiện ở một số
                tuần-máy-chủ trong khoảng, làm hàng đó render THIẾU cột và lệch
                khỏi header). renderCell tự tra `emp.days` bằng NGÀY (không còn
                theo vị trí), xem khai báo hàm. */}
            {viewDates.map((_, dayIdx) => renderCell(emp, dayIdx, currentRow))}
            <td className={styles.colTotal} role="gridcell">
              <span
                className={`${styles.totalCol} ${emp.is_over_cap ? styles.totalColOverCap : ""}`}
              >
                {emp.is_over_cap && (
                  <PortalTooltip text="Vượt trần giờ làm" ariaLabel="Vượt trần giờ làm">
                    <WarnIcon size={15} />
                  </PortalTooltip>
                )}
                {fmtHours(emp.total_hours)} giờ
              </span>
            </td>
          </tr>
        );
      });
    return (
      <div className={styles.gridWrap}>
        <table className={styles.grid} role="treegrid" aria-label={gridAriaLabel}>
          <thead>
            <tr role="row">
              <th className={styles.colEmp} scope="col" role="columnheader">
                Nhân viên
              </th>
              {viewDates.map((day) => (
                <th key={day.date} className={styles.colDay} scope="col" role="columnheader">
                  {day.day_label} <small>{day.date.split("-")[2]}</small>
                </th>
              ))}
              <th className={styles.colTotal} scope="col" role="columnheader">
                <span className={styles.colTotalInner}>
                  Tổng
                  <PortalTooltip text={TOTAL_TOOLTIP} className={styles.infoIco} tabIndex={0} ariaLabel={TOTAL_TOOLTIP}>
                    <InfoIcon />
                  </PortalTooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map((group) => {
              const key = group.work_area_id ?? "unassigned";
              // ISS-MGR-6 (2026-08-09) LỖI THỜI, thay 2026-09-07: quyết định
              // cũ ẩn hẳn header cho nhóm work_area_id null. Wireframe hiện tại
              // (§SC-009 [11b] dòng ~3503-3504) xác nhận rõ nhóm phần dư này
              // PHẢI có header riêng, nhãn = "{tên cơ sở}" (backend _staff_area
              // đã fallback đúng tên chi nhánh khi work_area_id null — xem
              // manager_schedule.py, KHÔNG còn là "Chưa phân khu vực" chung
              // chung khi branch_name có sẵn) và đứng ĐẦU bảng (sort đã có sẵn,
              // không đổi). Bỏ nhánh flat-render, mọi nhóm render đồng nhất.
              const isCollapsed = !!collapsed[key];
              return (
                <React.Fragment key={key}>
                  <tr className={styles.deptGroup} role="row" aria-expanded={!isCollapsed}>
                    <th colSpan={viewDayCount + 2} scope="colgroup" role="rowheader">
                      <button
                        type="button"
                        className={styles.deptToggle}
                        aria-expanded={!isCollapsed}
                        onClick={() => toggleGroup(key)}
                      >
                        <span
                          className={`${styles.deptToggleChev} ${isCollapsed ? styles.deptToggleChevCollapsed : ""}`}
                          aria-hidden="true"
                        >
                          <ChevronDownIcon />
                        </span>
                        {group.work_area_name}{" "}
                        <span className={styles.deptCount}>({group.staff_count})</span>
                      </button>
                    </th>
                  </tr>
                  {!isCollapsed && renderEmployeeRows(group.employees)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  /** Mobile <768: view-theo-ngày — day chips T2..CN + cards gom bộ phận */
  const renderMobileDayview = () => {
    const days = viewDates;
    const safeIdx = Math.min(mobileDayIdx, Math.max(0, days.length - 1));
    const activeDay = days[safeIdx];
    return (
      <div className={styles.mobileDayview}>
        <div className={`${styles.txtSm} ${styles.muted}`} style={{ marginBottom: "var(--nqh-space-2)" }}>
          Chọn ngày:
        </div>
        <div className={styles.dayChips} role="tablist" aria-label="Chọn ngày trong khoảng đang xem">
          {days.map((day, idx) => (
            <button
              key={day.date}
              type="button"
              className={`${styles.dayChip} ${idx === safeIdx ? styles.dayChipActive : ""}`}
              role="tab"
              aria-selected={idx === safeIdx}
              onClick={() => setMobileDayIdx(idx)}
            >
              {day.day_label} {day.date.split("-")[2]}
            </button>
          ))}
        </div>
        {activeDay && (
          <div style={{ marginTop: "var(--nqh-space-4)", fontWeight: "var(--nqh-weight-semibold)" }}>
            Ca ngày {activeDay.day_label} {formatDayMonth(activeDay.date)}:
          </div>
        )}
        {filteredGroups.map((group) => (
          <React.Fragment key={group.work_area_id ?? "unassigned"}>
            {/* ISS-MGR-6 (2026-08-09) LỖI THỜI, thay 2026-09-07 — xem cùng lý
                do đã sửa ở renderGrid(): wireframe [11b] xác nhận nhóm phần dư
                PHẢI có header riêng (nhãn = tên cơ sở khi backend fallback được),
                không ẩn hẳn. */}
            <div className={styles.deptCardsHead}>
              <span className={styles.deptToggleChev} aria-hidden="true">
                <ChevronDownIcon />
              </span>
              {group.work_area_name} <span className={styles.deptCount}>({group.staff_count})</span>
            </div>
            {group.employees.map((emp) => {
              // Rủi ro 2 (2026-09-14 tiếp): tra `emp.days` bằng NGÀY
              // (`activeDay.date`, nguồn chung `viewDates`) thay vì
              // `emp.days[safeIdx]` theo VỊ TRÍ — cùng lý do đã sửa ở
              // renderCell()/renderGrid() phía trên: mảng `emp.days` của 1 NV
              // có thể ngắn hơn/lệch thứ tự `viewDates` khi NV chỉ xuất hiện ở
              // một số tuần-máy-chủ trong khoảng — index cũ có thể lấy NHẦM
              // NGÀY KHÁC (không chỉ trống — sai dữ liệu hiển thị) thay vì chỉ
              // ẩn thẻ như `if (!day) return null` bên dưới vẫn xử lý đúng cho
              // trường hợp thật-sự-không-có-dữ-liệu.
              const day = activeDay ? emp.days.find((d) => d.date === activeDay.date) : undefined;
              if (!day) return null;
              const isViolation = day.warnings.length > 0;
              return (
                <button
                  key={emp.user_id}
                  type="button"
                  className={`${styles.shiftCard} ${isViolation ? styles.shiftCardViolation : ""} ${
                    day.isPastWeek ? styles.shiftCardReadonly : ""
                  }`}
                  onClick={(e) => {
                    if (day.isPastWeek) return;
                    openCellPopover(emp, day, e.currentTarget);
                  }}
                >
                  <div className={styles.shiftCardHead}>
                    <span className={styles.shiftCardName}>
                      {formatEmployeeLabel(emp.employee_code, emp.full_name)}
                      {emp.transferred_from_branch_name && (
                        <span className={styles.empTransfer}>
                          điều chuyển từ {emp.transferred_from_branch_name}
                        </span>
                      )}
                    </span>
                    <span className={styles.infoIco} aria-hidden="true">
                      <DotsIcon />
                    </span>
                  </div>
                  {day.shifts.length === 0 ? (
                    <div className={styles.shiftCardShift}>
                      <span className={styles.muted}>— Nghỉ</span>
                    </div>
                  ) : (
                    day.shifts.map((shift) => (
                      <div className={styles.shiftCardShift} key={shift.schedule_id}>
                        <span
                          className={`${styles.shiftChip} ${
                            // Ca ngày nghỉ → chip xám "Ca nghỉ"; khối "Phân
                            // theo giờ" → màu theo contentChipClass (xem [OT]).
                            shift.is_day_off ? styles.shiftChipDayOff : contentChipClass(shift)
                          } ${approvalChipClass(shift.approval_status)}`}
                        >
                          {shift.is_day_off ? "Ca nghỉ" : (shift.shift_code ?? shift.shift_label)}
                          {!shift.is_day_off && !shift.shift_code && shift.is_overtime ? " · OT" : ""}
                        </span>{" "}
                        {shift.is_day_off ? (
                          shiftNameOf(shift)
                        ) : shift.shift_code ? (
                          // Có mã ca thật (khớp danh mục) — hiện tên ca + giờ,
                          // KHÔNG lặp lại giờ nếu chip đã tự hiện đủ (chỉ khi
                          // shift_code null, xem nhánh dưới).
                          `${shiftNameOf(shift)} · ${shift.start_time}–${shift.end_time}`
                        ) : (
                          // [OT] khối "Phân theo giờ" (shift_code null) — chip
                          // đã hiện đủ HH:mm–HH:mm (+ OT), KHÔNG lặp lại giờ ở
                          // ngoài.
                          ""
                        )}
                        {shift.approval_status === "PENDING" && (
                          <span className={styles.muted}> · Chờ duyệt</span>
                        )}
                        {shift.approval_status === "EXPIRED" && (
                          <span className={styles.muted}> · Hết hạn</span>
                        )}
                        {shift.approval_status === "REJECTED" && (
                          <span className={styles.rejectedLabel}> · Bị từ chối</span>
                        )}
                      </div>
                    ))
                  )}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ══════════════════════════════ RENDER ══════════════════════════════

  return (
    <>
      {/* [7] Tiêu đề vùng + [8] badge — dòng tiêu đề CHỈ có "Bảng phân ca" +
          badge trạng thái + CHỈ nút [Xuất bản] (ANNOTATION [1a]).
          Nhập/Nhập nhanh/Xuất ▼/Sao chép lịch nằm trong toolbar [1a] bên
          dưới (cụm hành động bên phải, sau khoảng giãn). */}
      <div className={styles.pageHead}>
        <h1 className={styles.pageHeadTitle}>Bảng phân ca</h1>
        {renderBadge()}
        <div className={styles.pageHeadActions}>
          {/* "N đề xuất chờ duyệt" đặt sát nút Xuất bản để nhắc người phân ca
              xử lý đề xuất trước khi công bố tuần. */}
          {pendingTotal > 0 && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnAccent}`}
              onClick={() => setWeekApprovalOpen(true)}
            >
              {pendingTotal} đề xuất chờ duyệt
            </button>
          )}
          {/* Hệ thống đã autosave từng ô ngay khi gán (POST .../cells commit ngay) —
              không có nút "Lưu nháp" riêng; endpoint save-draft giữ nguyên, không còn caller. */}
          {!isPastWeek && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}${publishVisuallyLocked ? ` ${styles.btnLocked}` : ""}`}
              disabled={publishHardDisabled}
              title={publishTitle}
              onClick={openPublishDialog}
            >
              {/* BUG THẬT đã sửa (2026-08-09): trước đây dùng `isPublished`
                  (true cho CẢ "đã xuất bản, không đổi gì" LẪN "đã xuất bản,
                  có sửa chưa công bố") → nút luôn hiện "Xuất bản lại" ngay
                  cả khi không có gì để xuất lại, mâu thuẫn với badge bên
                  cạnh (đã đúng, dùng riêng dirty_after_publish/anyDirty).
                  Nhãn nút giờ khớp badge: chỉ "Xuất bản lại" khi THẬT SỰ có
                  tuần nào đó trong khoảng còn thay đổi chưa công bố
                  (`guardLocked`, gộp nhiều tuần — xem khai báo phía trên). */}
              {isPublishingBatch && <span className={styles.spinner} aria-hidden="true" />}
              {guardLocked ? "Xuất bản lại" : isPublished ? "Đã xuất bản" : "Xuất bản"}
            </button>
          )}
        </div>
      </div>

      {/* [6] Panel Nhật ký ĐẨY khối nội dung (banner/toolbar-nội dung dưới/
          lưới/legend) sang trái, KHÔNG che — `.scheduleLayoutMain` bọc từ đây
          tới hết `.legend` (KHÔNG bọc `.pageHead` — tiêu đề trang giữ nguyên
          full-width phía trên, panel chỉ tác động khối nội dung dưới nó,
          khớp mockup panel [6] tự thân độc lập với toolbar/tiêu đề). */}
      <div className={styles.scheduleLayout}>
      <div className={styles.scheduleLayoutMain}>
      {/* [20] Banner tuần đã qua — chỉ xem */}
      {isPastWeek && (
        <div className={`${styles.banner} ${styles.bannerLock}`} role="status" style={{ marginBottom: "var(--nqh-space-4)" }}>
          <span className="ico" aria-hidden="true">
            <LockIcon />
          </span>
          <span>Tuần đã qua — chỉ xem, không chỉnh sửa</span>
        </div>
      )}

      {/* [1a] Thanh công cụ — MỘT DÒNG DUY NHẤT theo ANNOTATION [1a]:
          [🔍 Nhân viên ▼] · [Lọc khu vực ▼] · ‹ [khoảng ngày] › · [Tuần này] ·
          (giãn) · [Nhập] · [Nhập nhanh] · [Xuất ▼] · [Sao chép lịch]. Không có
          [Nhật ký] — "Nhật ký thay đổi lịch" (BR-SCHD-012, wireframe [6]) ĐÃ CÓ
          backend (`GET /manager/schedule/audit-history`, manager_schedule.py) —
          nút chưa lên ở lượt này vì backend đang được mở rộng để phân biệt đủ
          11 route/loại thao tác wireframe [6] yêu cầu (trước đó chỉ lọc 4/13
          action); FE sẽ nối nút [Nhật ký] ở lượt kế tiếp khi response đã đủ
          field `route`/`content_kind`. "Đơn vị: {branchLabel}" không có
          trong wireframe [1a] — vẫn hiện trong DIALOG XÁC NHẬN XUẤT BẢN [12]
          (confirmPublish bên dưới, dùng `branchLabel`). */}
      <div className={styles.filterBar}>
        {/* [🔍 Nhân viên ▼] — wireframe [11c] "GỘP 2-TRONG-1": đúng 1 control,
            ô tìm CHỈ nằm trong tấm (không có ô tìm rời trên toolbar); nhãn nút
            nêu đủ cả hai vế (chọn NV cụ thể + chuỗi tìm) để không mất dấu bộ
            lọc khi tấm đã đóng. */}
        <span className={styles.menuAnchor}>
          <button
            type="button"
            className={`${styles.selectBtn} ${employeeFilterActive ? styles.selectBtnActive : ""}`}
            aria-haspopup="true"
            aria-expanded={openMenu === "employee"}
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setMenuAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
              setOpenMenu(openMenu === "employee" ? null : "employee");
            }}
            {...guardProps}
          >
            <span>Nhân viên: {employeeFilterLabel}</span>
            <ChevronDownIcon />
          </button>
          {openMenu === "employee" && menuAnchor && (
            <PortalMenu
              anchor={menuAnchor}
              onClose={() => setOpenMenu(null)}
              ariaLabel="Lọc nhân viên"
              minWidth={340}
              style={{
                width: 340,
                padding: "var(--nqh-space-3)",
                display: "flex",
                flexDirection: "column",
                gap: "var(--nqh-space-2)",
              }}
            >
              {/* A2 (2026-09-03): stopPropagation phòng thủ để thao tác trong
                 picker (gõ tìm, tick checkbox) không vô tình bị đóng bởi handler
                 click nào khác ở cấp cao hơn — KHÔNG còn phải né listener
                 "click → đóng menu" của trang (đã GỠ 2026-09-14, xem comment
                 ngay trên khai báo state `menuAnchor`/effect kế bên phía trên
                 trong component): PortalMenu tự đóng khi click NGOÀI qua
                 `mousedown` + `ref.contains()`, không đụng tới click TRONG. */}
              <div onClick={(e) => e.stopPropagation()} style={{ display: "contents" }}>
                {/* prototype menuEmp() (sc009-build-prototype.html dòng
                    ~2787-2795): "Tất cả (bỏ chọn)" là `menu__item` RIÊNG, đứng
                    NGOÀI/TRÊN tấm — KHÔNG nằm trong EmployeePicker (khác
                    wireframe [11c] lỗi thời, đã port nhầm nút này vào TRONG
                    component). Chỉ bỏ SELECTION, giữ nguyên chuỗi tìm — đúng
                    `ST.filters.emps = 'ALL'` của prototype (không đụng
                    `ST.filters.search`). */}
                <button
                  type="button"
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={() => setSelectedEmployeeIds([])}
                >
                  Tất cả (bỏ chọn)
                </button>
                {/* ⚠️ Bộ lọc này là VIEW-scope THUẦN — KHÔNG phải phạm vi ghi.
                    `selectedEmployeeIds` KHÔNG truyền vào ShiftCellPopover (xem
                    `ActiveCell.rangeEmployeeIds` + chỗ gọi popover bên dưới);
                    phạm vi ghi hàng loạt CHỈ đến từ kéo-chọn vùng [2b]. */}
                <EmployeePicker
                  items={employeePickerItems}
                  selectedIds={new Set(selectedEmployeeIds)}
                  onToggle={toggleEmployeeFilter}
                  onSetGroup={setEmployeeGroupFilter}
                  search={employeeSearch}
                  onSearchChange={setEmployeeSearch}
                  onClearAll={clearEmployeeFilter}
                  searchPlaceholder="Tìm theo tên hoặc Mã NV..."
                />
              </div>
            </PortalMenu>
          )}
        </span>
        {/* [Lọc khu vực ▼] — lọc thật qua param work_area_id */}
        <span className={styles.menuAnchor}>
          <button
            type="button"
            className={styles.selectBtn}
            aria-haspopup="true"
            aria-expanded={openMenu === "area"}
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setMenuAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
              setOpenMenu(openMenu === "area" ? null : "area");
            }}
            {...guardProps}
          >
            <span>
              Khu vực:{" "}
              {transferredOnly
                ? `Chỉ NV điều chuyển (${transferredCount})`
                : areaId
                  ? areaOptions.find((a) => a.id === areaId)?.name ?? "Tất cả"
                  : "Tất cả"}
            </span>
            <ChevronDownIcon />
          </button>
          {openMenu === "area" && menuAnchor && (
            <PortalMenu
              anchor={menuAnchor}
              onClose={() => setOpenMenu(null)}
              ariaLabel="Lọc khu vực"
              minWidth={200}
              style={{ padding: "var(--nqh-space-1)" }}
            >
              <button
                type="button"
                className={`${styles.menuItem} ${areaId === "" && !transferredOnly ? styles.menuItemActive : ""}`}
                role="menuitem"
                onClick={() => {
                  setAreaId("");
                  setTransferredOnly(false);
                  setOpenMenu(null);
                }}
              >
                Tất cả
              </button>
              {areaOptions.map((area) => (
                <button
                  key={area.id || "unassigned"}
                  type="button"
                  className={`${styles.menuItem} ${!transferredOnly && areaId === area.id ? styles.menuItemActive : ""}`}
                  role="menuitem"
                  onClick={() => {
                    setAreaId(area.id);
                    setTransferredOnly(false);
                    setOpenMenu(null);
                  }}
                >
                  {area.name}
                </button>
              ))}
              {/* WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 [11b] dòng ~3505:
                  "mục lọc riêng 'Chỉ NV điều chuyển (N)' ở cuối menu (ẩn khi
                  N=0)" — lọc CLIENT-SIDE (transferred_from_branch_name), loại
                  trừ với chọn khu vực thường (không phải work_area_id thật). */}
              {transferredCount > 0 && (
                <>
                  <div className={styles.menuSep} role="separator" />
                  <button
                    type="button"
                    className={`${styles.menuItem} ${transferredOnly ? styles.menuItemActive : ""}`}
                    role="menuitem"
                    onClick={() => {
                      setAreaId("");
                      setTransferredOnly(true);
                      setOpenMenu(null);
                    }}
                  >
                    Chỉ NV điều chuyển ({transferredCount})
                  </button>
                </>
              )}
            </PortalMenu>
          )}
        </span>
        {/* [1a-range]/[CAL] Cụm chọn khoảng ngày (2026-09-14, gộp "component
            bộ lọc Calendar" + BỎ dropdown "Xem: Tuần ▼" — xác nhận trực tiếp
            coordinator, xem header file): `‹`/`›` bước ±7 ngày GIỮ NGUYÊN N
            (shiftRange, không mở lịch); nhãn giữa (ContinuousScrollDatePicker)
            mở [CAL] để chọn khoảng tuỳ ý, trần CAL_MAX_RANGE_DAYS. Lưới LUÔN
            sửa được bất kể N (ANNOTATION [1]) — không còn nhánh chỉ-xem. */}
        <div className={styles.weekNav}>
          <button
            type="button"
            className={styles.btnIcon}
            aria-label="Tuần trước"
            onClick={() => shiftRange(-7)}
            {...guardProps}
          >
            <ChevronLeftIcon />
          </button>
          {/* [1e] Nhãn phạm vi — khuôn "{dd/MM} – {dd/MM}/{yyyy} · {N} ngày",
              KHÔNG hiện "Tuần {N}" (wireframe §SC-009 [1e]: "Khi N=7 trùng
              đúng tuần lịch VẪN GIỮ khuôn này — tránh state ẩn khiến cùng một
              phạm vi lúc hiện số tuần lúc không") — MỘT khuôn cho MỌI N.
              [1a-range] "giữ icon lịch" — ContinuousScrollDatePicker.tsx tự
              hiện `{label} ▾`, không có khe icon riêng; icon lịch đặt NGOÀI,
              ngay trước trigger, cùng cụm `.weekNav`, trang trí thuần
              (aria-hidden), không đổi vùng bấm mở lịch. */}
          <CalendarIcon />
          <ContinuousScrollDatePicker
            value={rangeFromISO === rangeToISO ? { mode: "single", date: rangeFromISO } : { mode: "range", from: rangeFromISO, to: rangeToISO }}
            onApply={applyCalSelection}
            maxRangeDays={CAL_MAX_RANGE_DAYS}
            triggerClassName={styles.weekNavLabel}
            triggerLabel={rangeLabel}
            ariaLabel="Chọn khoảng ngày xem lịch"
          />
          <button
            type="button"
            className={styles.btnIcon}
            aria-label="Tuần sau"
            onClick={() => shiftRange(7)}
            {...guardProps}
          >
            <ChevronRightIcon />
          </button>
        </div>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSecondary}`}
          onClick={resetToCurrentWeek}
          {...guardProps}
        >
          Tuần này
        </button>
        {/* (giãn) — đẩy cụm hành động Nhập/Nhập nhanh/Xuất/Sao chép lịch về
            phải khi thanh còn dư chỗ; co về 0 khi tràn khung (cuộn ngang). */}
        <span className={styles.filterBarSpacer} aria-hidden="true" />
        {/* [Nhập] — backend (`weekly-grid/import/preview|commit`) chỉ nhận
            `week_start` ĐƠN (không có endpoint gộp nhiều tuần) — chỉ hiện
            khi khoảng đang xem khớp ĐÚNG 1 tuần máy chủ (`isSingleWeekView`),
            tránh áp dụng NGẦM chỉ 1 phần khoảng đang hiển thị. Ẩn khi tuần đó
            đã qua (chỉ đọc), cùng điều kiện cũ. */}
        {!isPastWeek && isSingleWeekView && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setImportOpen(true)}
            {...guardProps}
          >
            Nhập
          </button>
        )}
        {/* [Nhập nhanh] — BR-SCHD-011 (2026-09-07, 🆕 CHƯA CÓ BR chính thức —
            wireframe là nguồn đầu tiên): khả dụng cho MỌI N (backend nhận
            danh sách assignment rời, không cần 1 tuần đơn — xem
            QuickEntryModal/quickEntryRoster), ẩn khi khoảng đã qua hẳn. */}
        {!isPastWeek && !rangeInvalid && (
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setQuickEntryOpen(true)}
            {...guardProps}
          >
            Nhập nhanh
          </button>
        )}
        {/* [Xuất ▼] — Rủi ro 3 GIẢI (xem comment handleExport phía trên): CHỈ
            ĐỌC nên KHÔNG còn khoá theo `isSingleWeekView` — hiện bất cứ khi
            nào có tuần-máy-chủ nào để xuất (`touchedWeekStarts`), kể cả khoảng
            trải nhiều tuần (mỗi tuần tải 1 file riêng). [Nhập] vẫn giữ khoá cũ
            — xem comment ngay phía trên (rủi ro GHI, chưa có cách tách file
            theo tuần an toàn). */}
        {!rangeInvalid && touchedWeekStarts.length > 0 && (
          <span className={styles.menuAnchor}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              aria-haspopup="true"
              aria-expanded={openMenu === "export"}
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setMenuAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
                setOpenMenu(openMenu === "export" ? null : "export");
              }}
              {...guardProps}
            >
              Xuất
              <ChevronDownIcon />
            </button>
            {openMenu === "export" && menuAnchor && (
              <PortalMenu
                anchor={menuAnchor}
                onClose={() => setOpenMenu(null)}
                ariaLabel="Xuất lịch"
                minWidth={200}
                style={{ padding: "var(--nqh-space-1)" }}
              >
                <button type="button" className={styles.menuItem} role="menuitem" onClick={() => handleExport("xlsx")}>
                  Xuất Excel (.xlsx){touchedWeekStarts.length > 1 ? ` — ${touchedWeekStarts.length} tuần` : ""}
                </button>
                <button type="button" className={styles.menuItem} role="menuitem" onClick={() => handleExport("csv")}>
                  Xuất CSV (.csv){touchedWeekStarts.length > 1 ? ` — ${touchedWeekStarts.length} tuần` : ""}
                </button>
              </PortalMenu>
            )}
          </span>
        )}
        {/* [Sao chép lịch] (2026-09-14, tiếp #3) — wireframe [17]/[17b] +
            prototype `openCopyMenu`/`openCopyModal`: menu 3 nguồn ("tuần
            trước"/"tháng trước"/"theo khoảng ngày…") → CopyScheduleModal
            (khoảng NGUỒN/ĐÍCH tuỳ ý, N≠M lặp chu kỳ, bắt buộc xem trước).
            Mờ (KHÔNG `disabled` — xem `isCopyTargetFullyPast`) khi khoảng
            đích đang xem đã qua hẳn; vẫn clickable → toast, khớp prototype. */}
        <span className={styles.menuAnchor}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}${isCopyTargetFullyPast ? ` ${styles.btnLocked}` : ""}`}
            aria-haspopup="true"
            aria-expanded={openMenu === "copy"}
            onClick={openCopyMenu}
            {...guardProps}
          >
            Sao chép lịch
            <ChevronDownIcon />
          </button>
          {openMenu === "copy" && menuAnchor && (
            <PortalMenu anchor={menuAnchor} onClose={() => setOpenMenu(null)} ariaLabel="Sao chép lịch" minWidth={220} style={{ padding: "var(--nqh-space-1)" }}>
              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => chooseCopySource("week")}>
                Sao chép tuần trước
              </button>
              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => chooseCopySource("month")}>
                Sao chép tháng trước
              </button>
              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => chooseCopySource("range")}>
                Sao chép theo khoảng ngày…
              </button>
            </PortalMenu>
          )}
        </span>
        {/* [Nhật ký] (2026-09-14, tiếp #4) — BR-SCHD-012, panel [6] "Nhật ký
            thay đổi lịch". LUÔN hiện với MỌI vai vào được màn, kể cả vai chỉ
            đọc (ANNOTATION [6]: "Xem Nhật ký không phải quyền ghi") — KHÔNG
            `guardProps` (khoá D-6 chỉ áp cho thao tác GHI), KHÔNG điều kiện
            `isPastWeek`/role, khác mọi nút khác trên thanh này. */}
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSecondary}`}
          aria-pressed={auditOpen}
          onClick={() => setAuditOpen((v) => !v)}
        >
          Nhật ký
        </button>
      </div>

      {/* [4] Cảnh báo tổng hợp — theo yêu cầu 2026-08-17: KHÔNG chiếm 1 dòng dài
          cố định trên UI nữa, chỉ để icon nhỏ; di chuột/focus vào mới hiện chi
          tiết đầy đủ qua PortalTooltip (ẩn khi không vi phạm; tuần quá khứ →
          "(lịch sử)", giữ nguyên nội dung chi tiết, chỉ đổi cách hiển thị). */}
      {warningsSummaryText && (
        <PortalTooltip
          text={
            isPastWeek
              ? warningsSummaryText.replace("cảnh báo:", "cảnh báo (lịch sử):")
              : warningsSummaryText
          }
          className={`${styles.warningIco} ${styles.bannerWarning}`}
          tabIndex={0}
          ariaLabel={`${warningsCount} cảnh báo — di chuột để xem chi tiết`}
        >
          <WarnIcon />
          <span className={styles.warningCount}>{warningsCount}</span>
        </PortalTooltip>
      )}

      {/* [21] Dòng xác nhận đã gửi thông báo — HIỆN sau khi xuất bản */}
      {publishNotice && (
        <div className={`${styles.banner} ${styles.bannerInfo}`} role="status" style={{ marginBottom: "var(--nqh-space-4)" }}>
          <span className="ico" aria-hidden="true">
            <MailIcon />
          </span>
          <span>Đã gửi thông báo tới nhân viên trong đơn vị.</span>
        </div>
      )}

      {/* [19] Gợi ý xếp ca — tuần chưa có ca nào */}
      {isEmptyWeek && !isPastWeek && (
        <div className={styles.hintRow} role="status" style={{ marginBottom: "var(--nqh-space-4)" }}>
          <span className="ico" aria-hidden="true">
            <InfoIcon size={18} />
          </span>
          <span>Chưa xếp ca cho tuần này. Bấm vào ô để thêm ca, hoặc Nhập từ tệp.</span>
        </div>
      )}

      {/* ══════════ [5] LƯỚI phân ca + view-theo-ngày mobile — MỘT nguồn dữ
          liệu duy nhất (weekQueries/mergedGroups) bất kể N ngày (2026-09-14,
          xoá nhánh "Nhiều tuần (danh sách) chỉ-xem" cũ) ══════════ */}
      {rangeInvalid ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          Ngày kết thúc phải sau ngày bắt đầu.
        </div>
      ) : weeksLoading ? (
        <div aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.skeletonRow}`} />
          ))}
        </div>
      ) : weeksError ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          Không tải được dữ liệu. Thử lại
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            style={{ marginLeft: "auto" }}
            onClick={refetchWeeks}
          >
            Thử lại
          </button>
        </div>
      ) : mergedGroups.length === 0 ? (
        <div className={styles.emptyState}>Không có nhân viên để hiển thị.</div>
      ) : /* wireframe [11c]/[11d] STATE `TÌM-TÊN-KHÔNG-KHỚP`: chuỗi tìm trong
           tấm không khớp AI CẢ (kể cả ngoài phạm vi selectedEmployeeIds hiện
           tại) → thông báo RIÊNG kèm [Xoá ô tìm] — CHỈ xoá chuỗi tìm, GIỮ
           NGUYÊN bộ lọc khu vực/nhân viên đang chọn. Ưu tiên kiểm TRƯỚC
           nhánh "rỗng theo bộ lọc" chung bên dưới (cụ thể hơn). Áp cho MỌI
           độ dài khoảng (wireframe [11c] "Hai vế kết hợp AND ... áp cho mọi
           độ dài khoảng"). */
      searchMatchedEmployeeIds && searchMatchedEmployeeIds.size === 0 ? (
        <div className={styles.emptyState}>
          Không có nhân viên nào khớp "{employeeSearchTrimmed}".{" "}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            onClick={() => setEmployeeSearch("")}
          >
            Xoá ô tìm
          </button>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className={styles.emptyState}>
          Không có nhân viên nào khớp bộ lọc đã chọn.{" "}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            onClick={clearEmployeeFilter}
          >
            Xoá bộ lọc nhân viên
          </button>
        </div>
      ) : (
        <>
          {renderGrid()}
          {renderMobileDayview()}
        </>
      )}

      {/* [LEGEND] Chú thích — wireframe ANNOTATION
          [LEGEND]: "Đúng 7 dòng, chỉ ký hiệu MÀU/VIỀN phân biệt loại ca (sáng
          · chiều · đêm · phân theo giờ · OT · chờ duyệt · điều động)". Đặt
          NGOÀI mọi nhánh tải/lỗi/rỗng ở trên — "luôn hiển thị", cố định cuối
          trang dưới lưới, cuộn theo trang (KHÔNG phải overlay/position:fixed).
          Cố ý KHÔNG thêm dòng cho Hết hạn/Ca nghỉ/Từ chối — wireframe liệt rõ
          "Không liệt ký hiệu hành vi/cảnh báo trong chú thích" (badge trạng
          thái đã có tooltip riêng gắn thẳng trên badge). */}
      <div className={styles.legend} aria-label="Chú thích màu chip ca">
        <div className={styles.legendTitle}>Chú thích</div>
        <div className={styles.legendGrid}>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.shiftChipMorning}`} aria-hidden="true" />
            Ca sáng
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.shiftChipAfter}`} aria-hidden="true" />
            Ca chiều
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.shiftChipNight}`} aria-hidden="true" />
            Ca đêm
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.shiftChipHourly}`} aria-hidden="true" />
            Phân theo giờ
          </span>
          <span className={styles.legendItem}>
            {/* Swatch riêng .legendSwatchOt (viền-trong turmeric-50/900) —
                KHÁC .shiftChipOt (fill đặc) dùng cho chip thật trên lưới.
                Khớp đúng sc009-build-prototype.html .legend__swatch của dòng
                OT; không dùng chung class với chip thật nữa (2 nơi khác ý đồ
                nhau: swatch minh hoạ vs chip render dữ liệu thật). */}
            <span className={`${styles.legendSwatch} ${styles.legendSwatchOt}`} aria-hidden="true" />
            OT (ngoài giờ) — khác màu Phân theo giờ
          </span>
          <span className={styles.legendItem}>
            {/* Đối chiếu 2026-09-14 (@coder, PO xác nhận): prototype
                (text + border:1px dashed var(--nqh-sky-700)) VÀ khung ASCII
                wireframe ("viền nét đứt xanh") ĐỒNG THUẬN — không phải
                wireframe tự mâu thuẫn như nghi ngờ trước đây. Đoạn prose
                [1-chip] của wireframe chỉ im lặng về màu (không nói gì
                thêm, không phủ định "xanh"), không phải một nguồn đối lập.
                PO ratify: 2/2 nguồn thiết kế đồng thuận → sửa swatch + copy
                khớp đúng "xanh"/sky-700. */}
            <span className={`${styles.legendSwatch} ${styles.legendSwatchPending}`} aria-hidden="true" />
            Chờ duyệt (viền nét đứt xanh, đè lên màu ca gốc)
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.legendSwatchCrossBranch}`} aria-hidden="true" />
            Điều động (chữ chi nhánh đậm coffee + viền nét đứt coffee)
          </span>
        </div>
      </div>
      </div>
      {auditOpen && (
        <ScheduleAuditPanel
          initialWeekStart={data?.week_start ?? toISODate(weekStartOf(parseISODate(rangeFromISO), weekStartDay))}
          weekStartDay={weekStartDay}
          branchName={data?.branch_name || data?.branch_code || null}
          onClose={() => setAuditOpen(false)}
        />
      )}
      </div>

      {/* ══════════ [13] POPOVER "Chọn ca" + [14] cảnh báo (trong popover) ══════════ */}
      {activeCell && activeCellData && !isPastWeek && (
        <ShiftCellPopover
          // [KEY-REMOUNT] `ShiftCellPopover` giữ state cục bộ chưa-lưu (staged,
          // selected, pendingDiscardWarning, editingShiftId...). Click sang 1
          // ô/vùng-chọn KHÁC chỉ đổi `activeCell` — nó KHÔNG tự đóng popover
          // đang mở trước (không đi qua onClose/handleRequestClose của chính
          // popover đó). Không có `key`, React coi đây là CÙNG instance ở
          // CÙNG vị trí cây và chỉ cập nhật props, giữ nguyên mọi state cục bộ
          // → state của ô/vùng CŨ rò rỉ sang popover đang hiển thị cho ô/vùng
          // MỚI (banner cảnh báo + danh sách chờ ghi sai chủ). `key` theo đúng
          // định danh mục tiêu (ô đơn hoặc vùng kéo-chọn) buộc React unmount
          // instance cũ + mount instance mới mỗi khi mục tiêu đổi — reset MỌI
          // state cục bộ (kể cả state thêm sau này) mà không cần liệt kê thủ
          // công từng cái trong 1 `useEffect`.
          key={`${activeCell.userId}-${activeCell.dateISO}-${activeCell.endDateISO ?? ""}-${(activeCell.rangeEmployeeIds ?? []).join(",")}`}
          anchorRect={activeCell.anchor}
          userId={activeCell.userId}
          employeeName={activeCellData.employeeName}
          day={activeCellData.day}
          initialRangeEnd={activeCell.endDateISO}
          shiftCodes={shiftCodesQuery.data?.items ?? []}
          onClose={() => setActiveCell(null)}
          onToast={setToast}
          onBulkAssigned={(result) => setToast(formatBulkAssignToast(result))}
          // Bộ lọc "Nhân viên" (`selectedEmployeeIds`) giữ đúng vai trò
          // VIEW-scope thuần (chỉ dùng cho `filteredGroups` ở trên) — KHÔNG
          // truyền vào ShiftCellPopover để mở rộng phạm vi ghi. Phạm vi ghi
          // hàng loạt chỉ đến từ kéo-chọn vùng [2b] (`rangeEmployeeIds` dưới).
          rangeEmployeeIds={activeCell.rangeEmployeeIds}
          rangeEmployeeNames={activeCell.rangeEmployeeNames}
          // PO 2026-09-13: dòng "Làm tại" trong danh sách chờ ghi cần tên chi
          // nhánh HIỆN TẠI mặc định (khi chưa chọn "Chi nhánh làm việc" khác)
          // — popover tự nó không biết tên chi nhánh đang xem (chỉ nhận
          // branchesQuery cho dropdown điều động), lấy từ `data.branch_name`
          // đã có sẵn ở đây (khớp nguồn `branchLabel` dòng ~555).
          currentBranchName={data?.branch_name ?? null}
          onBulkOff={(result) => setToast(formatBulkOffToast(result))}
        />
      )}

      {/* ══════════ [14b] DIALOG vi phạm ràng buộc (mode Chặn, D-6) ══════════
          Tái dùng pattern banner cảnh báo của popover [14] (ShiftCellPopover)
          — WarnIcon + styles.banner/bannerWarning + CHỈ 1 nút "Đã hiểu",
          KHÔNG có nút "Vẫn xuất bản": mode Chặn không cho override, y hệt
          quy tắc D-6 áp cho popover gán ca. Nhiều tuần vi phạm mode Chặn
          trong khoảng đang xem → liệt TỪNG tuần (KHÔNG publish tuần nào cả —
          suy luận riêng @coder, chưa có trong wireframe: an toàn hơn "publish
          phần sạch, chặn phần vi phạm" — xem PR description). */}
      {publishDialog === "blocked" && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="publishBlockedTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="publishBlockedTitle">
                Không thể xuất bản — còn vi phạm ràng buộc
              </h2>
              <button
                type="button"
                className={styles.btnIcon}
                aria-label="Đóng"
                onClick={() => setPublishDialog(null)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {publishableWeeks
                .filter((w) => w.rules.violation_mode === "block" && w.warnings_count > 0)
                .map((w) => (
                  <div
                    key={w.week_start}
                    className={`${styles.banner} ${styles.bannerWarning}`}
                    role="alert"
                    style={{ marginBottom: "var(--nqh-space-2)" }}
                  >
                    <span className="ico" aria-hidden="true">
                      <WarnIcon />
                    </span>
                    <span>
                      Tuần {formatWeekRange(w.week_start, w.week_end)}:{" "}
                      {w.warnings_summary ?? `${w.warnings_count} cảnh báo vi phạm ràng buộc.`}
                    </span>
                  </div>
                ))}
              <p style={{ marginTop: "var(--nqh-space-3)" }}>
                {publishableWeeks.length > 1
                  ? "Một hoặc nhiều tuần trong khoảng đang xem ở chế độ \"Chặn\" (violation_mode = block) — sửa các ô còn vi phạm trước khi có thể xuất bản. KHÔNG tuần nào trong khoảng được xuất bản cho tới khi hết vi phạm."
                  : `Lịch tuần ${weekRange} đang ở chế độ "Chặn" (violation_mode = block) — sửa các ô còn vi phạm trước khi có thể xuất bản.`}
              </p>
            </div>
            <div className={styles.modalFoot}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={() => setPublishDialog(null)}
              >
                Đã hiểu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ [15]/[16] DIALOG Xuất bản ══════════ wireframe [PUB]
          "Phạm vi xuất bản khi khoảng không trùng tuần lịch — dialog nêu rõ
          các tuần sẽ được xuất bản": liệt kê ĐÚNG `publishableWeeks`, cùng
          tuần rỗng/quá khứ bị BỎ QUA (skippedPublishWeeks) để không hiểu
          nhầm "khoảng đang nhìn" sẽ xuất bản trọn vẹn. */}
      {publishDialog && publishDialog !== "blocked" && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="publishTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="publishTitle">
                {publishDialog === "late"
                  ? "Xuất bản muộn hơn khuyến nghị"
                  : publishableWeeks.length === 1
                    ? `Xuất bản lịch tuần ${publishableWeeks[0].week_number}?`
                    : `Xuất bản lịch cho ${publishableWeeks.length} tuần?`}
              </h2>
              <button
                type="button"
                className={styles.btnIcon}
                aria-label="Đóng"
                onClick={() => setPublishDialog(null)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {publishError && (
                <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
                  <span>
                    {publishError.message}
                    {publishError.violations && publishError.violations.length > 0 && (
                      <>
                        {" "}
                        {publishError.violations.join("; ")}
                      </>
                    )}
                  </span>
                </div>
              )}
              {publishDialog === "late" && publishableWeeks[0] && (
                <p>
                  Tuần {formatWeekRange(publishableWeeks[0].week_start, publishableWeeks[0].week_end)} bắt đầu
                  sau {publishDaysAhead} ngày. Lịch nên được xuất bản trước tối thiểu{" "}
                  {publishableWeeks[0].rules.publish_ahead_days} ngày. Vẫn tiếp tục xuất bản?
                </p>
              )}
              <p>
                {publishableWeeks.length === 1
                  ? `Lịch tuần ${formatWeekRange(publishableWeeks[0].week_start, publishableWeeks[0].week_end)} (${branchLabel}) sẽ được công bố tới nhân viên.`
                  : `${publishableWeeks.length} tuần lịch sau (${branchLabel}) sẽ được công bố tới nhân viên:`}{" "}
                Nhân viên nhận thông báo và xem được ca của mình.
              </p>
              {publishableWeeks.length > 1 && (
                <ul style={{ margin: "var(--nqh-space-2) 0 0", paddingLeft: "var(--nqh-space-5)" }}>
                  {publishableWeeks.map((w) => (
                    <li key={w.week_start}>
                      {formatWeekRange(w.week_start, w.week_end)}
                      {w.dirty_after_publish ? " (xuất bản lại)" : w.status === "published" ? "" : " (mới)"}
                    </li>
                  ))}
                </ul>
              )}
              {skippedPublishWeeks.length > 0 && (
                <p className={`${styles.txtSm} ${styles.muted}`} style={{ marginTop: "var(--nqh-space-2)" }}>
                  Bỏ qua {skippedPublishWeeks.length} tuần (đã qua hoặc chưa có ca):{" "}
                  {skippedPublishWeeks.map((w) => formatWeekRange(w.week_start, w.week_end)).join(", ")}.
                </p>
              )}
            </div>
            <div className={styles.modalFoot}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setPublishDialog(null)}
              >
                Hủy
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={isPublishingBatch}
                onClick={confirmPublish}
              >
                {isPublishingBatch && <span className={styles.spinner} aria-hidden="true" />}
                {publishDialog === "late" ? "Vẫn xuất bản" : "Xuất bản"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ [17]→[18] MODAL Nhập lịch ══════════ Chỉ mở được khi
          `isSingleWeekView` (nút [Nhập] ẩn khi không đúng — xem toolbar),
          `data` luôn sẵn ở đây. */}
      {importOpen && data && (
        <ScheduleImportModal
          weekStart={data.week_start}
          weekNumber={data.week_number}
          onClose={() => setImportOpen(false)}
          onImported={(result) => setToast(formatImportResultToast(result))}
        />
      )}

      {/* ══════════ [5] MODAL "Nhập nhanh" (BR-SCHD-011) ══════════ khoảng áp
          dụng mặc định = ĐÚNG khoảng đang chọn trên thanh (wireframe [5]) —
          dùng thẳng rangeFromISO/rangeToISO, luôn khớp nhãn thanh công cụ vì
          CÙNG NGUỒN state (không còn 2 biến lệch nhau như bug cũ). */}
      {quickEntryOpen && (
        <QuickEntryModal
          employees={quickEntryRoster}
          shiftCodes={shiftCodesQuery.data?.items ?? []}
          defaultDateStart={rangeFromISO}
          defaultDateEnd={rangeToISO}
          onClose={() => setQuickEntryOpen(false)}
          onToast={setToast}
        />
      )}

      {/* ══════════ [17b] MODAL "Sao chép lịch" (2026-09-14, tiếp #3) ══════════
          Khoảng đích LUÔN = `rangeFromISO`/`rangeToISO` hiện tại (thanh công
          cụ) — modal chỉ hiển thị, không cho sửa (xem `chooseCopySource`). */}
      {copyModalSource && (
        <CopyScheduleModal
          initialSourceFrom={copyModalSource.from}
          initialSourceTo={copyModalSource.to}
          targetFrom={rangeFromISO}
          targetTo={rangeToISO}
          onClose={() => setCopyModalSource(null)}
          onToast={setToast}
        />
      )}

      {/* ══════════ Modal "Duyệt tuần" — Employee tự đăng ký ca (2026-08-09) ══════════ */}
      {weekApprovalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="weekApprovalTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="weekApprovalTitle">
                Đề xuất lịch chờ duyệt — Tuần {weekNumber}
              </h2>
              <button
                type="button"
                className={styles.btnIcon}
                aria-label="Đóng"
                onClick={() => {
                  setWeekApprovalOpen(false);
                  setWeekRejectingUserId(null);
                }}
              >
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* bot-037 (GH#803): tuần đã khoá — chỉ xem, không duyệt/từ chối
                  hàng loạt (cùng banner [20] ngoài trang). */}
              {isApprovalTargetWeekLocked && (
                <div className={`${styles.banner} ${styles.bannerLock}`} role="status" style={{ marginBottom: "var(--nqh-space-3)" }}>
                  <span className="ico" aria-hidden="true">
                    <LockIcon />
                  </span>
                  <span>Tuần đã qua — chỉ xem, không duyệt/từ chối được nữa.</span>
                </div>
              )}
              {pendingByEmployee.length === 0 ? (
                <div className={`${styles.txtSm} ${styles.muted}`}>Không còn đề xuất nào chờ duyệt.</div>
              ) : (
                pendingByEmployee.map((emp) => (
                  <div key={emp.user_id} style={{ marginBottom: "var(--nqh-space-4)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--nqh-space-2)" }}>
                      <span>
                        <strong>{emp.full_name}</strong> — {emp.count} ca đề xuất
                      </span>
                      <span style={{ display: "flex", gap: "var(--nqh-space-2)" }}>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                          disabled={weekApprovalMutation.isPending || isApprovalTargetWeekLocked}
                          onClick={() => handleApproveWeekFor(emp.user_id)}
                        >
                          Duyệt cả tuần
                        </button>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                          disabled={weekApprovalMutation.isPending || isApprovalTargetWeekLocked}
                          onClick={() => startWeekReject(emp.user_id)}
                        >
                          Từ chối cả tuần
                        </button>
                      </span>
                    </div>
                    {weekRejectingUserId === emp.user_id && (
                      <div style={{ marginTop: "var(--nqh-space-2)", display: "flex", flexDirection: "column", gap: "var(--nqh-space-2)" }}>
                        <textarea
                          className={styles.comboInput}
                          placeholder="Lý do từ chối cả tuần (bắt buộc)…"
                          rows={2}
                          value={weekRejectReason}
                          onChange={(e) => setWeekRejectReason(e.target.value)}
                          autoFocus
                        />
                        <span style={{ display: "flex", gap: "var(--nqh-space-2)", justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                            onClick={() => setWeekRejectingUserId(null)}
                          >
                            Huỷ
                          </button>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                            disabled={
                              weekApprovalMutation.isPending ||
                              isApprovalTargetWeekLocked ||
                              !weekRejectReason.trim()
                            }
                            onClick={confirmWeekReject}
                          >
                            Xác nhận từ chối cả tuần
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className={styles.modalFoot}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setWeekApprovalOpen(false)}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [18] Popup "Có thay đổi chưa công bố" — WIREFRAME-V0-MASTER-REGISTRY.md
          §SC-009 dòng 3350-3363 (mockup) + 3419 (ANNOTATION) + 3629-3635
          (STATE) + 3723 (chặn điều hướng). Góc dưới-phải cố định trên desktop;
          mobile dùng thanh dính đáy (ngoại lệ R7 có chủ đích, dòng 4029) vì
          phải nhìn thấy lưới phía sau để đối chiếu chỗ vừa sửa. */}
      {guardLocked && (
        <div className={styles.unpubPopup} role="alertdialog" aria-label="Có thay đổi chưa công bố">
          <div className={styles.unpubPopupHead}>
            <WarnIcon size={18} />
            <span style={{ flex: 1 }}>Có thay đổi chưa công bố</span>
            <button
              type="button"
              className={styles.btnIcon}
              aria-label={unpubCollapsed ? "Mở rộng" : "Thu gọn"}
              onClick={() => setUnpubCollapsed((v) => !v)}
            >
              {unpubCollapsed ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
          </div>
          {!unpubCollapsed && (
            <>
              <div className={styles.unpubPopupBody}>
                Bạn vừa sửa lịch đã xuất bản. Nhân viên đang thấy bản cũ.
              </div>
              {/* D-7 (2026-09-08): dòng giải thích NGAY TRONG popup, không chỉ
                  tooltip trên nút disable — tooltip cần hover, không hoạt động
                  trên mobile (nơi popup [18] hiện là thanh dính đáy, xem ghi
                  chú §[18] trên). */}
              {discardDisabledReason && (
                <div className={styles.unpubPopupNote}>{discardDisabledReason}</div>
              )}
              <div className={styles.unpubPopupFoot}>
                {/* Việc 1 (lượt 13, 2026-09-08): snapshot chụp NGAY SAU mỗi lần
                    [Xuất bản]/[Xuất bản lại] (backend publish_week() →
                    schedule_publish_snapshots, xem manager_schedule.py
                    discard_week_changes()) — "Hủy thay đổi" đọc lại bản GẦN
                    NHẤT đó, khôi phục sửa/xoá/thêm về đúng trạng thái đã
                    công bố. Disable khi backend báo `has_restorable_snapshot`
                    = false (tuần publish trước khi có snapshot) — tránh 422
                    "chưa từng xuất bản" xảy ra SAU khi bấm (ngõ cụt). */}
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  disabled={discardChangesMutation.isPending || !canRestoreSnapshot}
                  title={discardDisabledReason}
                  onClick={confirmDiscardChanges}
                >
                  Hủy thay đổi
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={openPublishDialog}
                >
                  Xuất bản lại
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Toast (pattern window.nqhToast) */}
      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </>
  );
};

export default ManagerSchedulePage;
